import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, Edit, Trash2, X, CheckSquare, Square, Download, 
  ChevronRight, Eye, DollarSign, Calendar, History, 
  CreditCard, Banknote, FileText, ArrowLeft, Check,
  Percent, Save, AlertCircle, Link2, Copy, Search, ShoppingBag, ShoppingCart, RotateCcw, KeyRound, Mail, Shuffle, EyeOff, UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { ComprovanteCell } from '../components/ComprovanteCell';
import AnimatedSearchBar from '../components/AnimatedSearchBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Formatar moeda
const formatCurrency = (value) => {
  return `R$ ${(value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const Sellers = () => {
  const navigate = useNavigate();
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSeller, setEditingSeller] = useState(null);
  
  // Visualização de detalhes do vendedor
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [sellerRomaneios, setSellerRomaneios] = useState([]);
  const [loadingRomaneios, setLoadingRomaneios] = useState(false);
  
  // Modal de detalhes do romaneio/comissão
  const [selectedRomaneio, setSelectedRomaneio] = useState(null);
  const [showComissaoModal, setShowComissaoModal] = useState(false);
  const [comissaoEditando, setComissaoEditando] = useState(null);
  
  // Modal de registrar pagamento de comissão
  const [showPagamentoModal, setShowPagamentoModal] = useState(false);
  const [pagamentoData, setPagamentoData] = useState({
    valor: '',
    data: new Date().toISOString().split('T')[0],
    forma_pagamento: 'pix',
    observacao: ''
  });
  const [abatimentos, setAbatimentos] = useState([{ valor: '', descricao: '' }]);
  const [showRomaneioAbatimento, setShowRomaneioAbatimento] = useState(false);
  const [romaneiosComoCliente, setRomaneiosComoCliente] = useState([]); // Romaneios onde o vendedor COMPROU (é cliente)
  
  // Seleção múltipla
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Pagamento em cascata (rodapé de Vendedores)
  const [cascataValor, setCascataValor] = useState('');
  const [cascataData, setCascataData] = useState(new Date().toISOString().split('T')[0]);
  const [cascataForma, setCascataForma] = useState('pix');
  const [cascataLoading, setCascataLoading] = useState(false);

  // Modal de Histórico Completo de Pagamentos
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);
  // Filtros e seleção do histórico
  const [histFilters, setHistFilters] = useState({ dataInicio: '', dataFim: '', romaneio: '', valorMin: '', valorMax: '', tipo: '' });
  const [histSelected, setHistSelected] = useState({}); // { key: true }

  // Modal de Selecionar Romaneio para abater
  const [showSelecionarRomaneioModal, setShowSelecionarRomaneioModal] = useState(false);
  const [cascataRomaneioSelecionado, setCascataRomaneioSelecionado] = useState(null);
  const [searchRomaneio, setSearchRomaneio] = useState('');

  // Lista principal de vendedores: busca + filtros
  const [searchTermSellers, setSearchTermSellers] = useState('');
  const [sellersFilters, setSellersFilters] = useState({
    pendenteMin: '',
    comComissao: '',
    comCompras: '',
  });

  // Recalcula totais do vendedor selecionado a partir dos romaneios atuais
  const refreshSelectedSeller = async () => {
    if (!selectedSeller) return;
    try {
      const [romaneiosResp, saldoResp] = await Promise.all([
        axios.get(`${API}/romaneios`),
        axios.get(`${API}/sellers/${selectedSeller.id}/saldo`).catch(() => ({ data: {} }))
      ]);
      const romaneios = romaneiosResp.data;
      const meus = romaneios.filter(r =>
        r.vendedor_id === selectedSeller.id || r.vendedor_nome === selectedSeller.nome
      );
      let totalComissao = 0, totalPago = 0;
      meus.forEach(rom => {
        let comissaoRom = 0;
        if (rom.items && rom.items.length > 0) {
          rom.items.forEach(item => {
            const c = item.percentual_comissao || selectedSeller.percentual_comissao || 0;
            comissaoRom += (item.valor_total || 0) * (c / 100);
          });
        } else {
          comissaoRom = (rom.valor_total || 0) * ((selectedSeller.percentual_comissao || 0) / 100);
        }
        totalComissao += comissaoRom;
        totalPago += rom.comissao_paga || 0;
      });
      setSelectedSeller(prev => ({
        ...prev,
        comissao_total_calculada: totalComissao,
        comissao_paga_calculada: totalPago,
        // Pendente nunca fica negativo: se pago > total, o excedente vira crédito da loja
        comissao_pendente_calculada: Math.max(0, totalComissao - totalPago),
        comissao_excedente_paga: Math.max(0, totalPago - totalComissao),
        total_vendas: meus.reduce((a, r) => a + (r.valor_total || 0), 0),
        qtd_vendas: meus.length,
        saldo_devedor_atual: (saldoResp.data?.saldo_devedor || 0) + Math.max(0, totalPago - totalComissao),
        saldo_devedor_real: saldoResp.data?.saldo_devedor || 0,
        historico_saldo: saldoResp.data?.historico_saldo || []
      }));
    } catch (e) {
      console.error('Erro ao atualizar totais do vendedor', e);
    }
  };

  const handlePagamentoCascata = async () => {
    const valor = parseFloat(String(cascataValor).replace(',', '.'));
    if (!valor || valor <= 0) {
      toast.error('Digite um valor válido');
      return;
    }
    if (!selectedSeller) return;
    setCascataLoading(true);
    try {
      // Se há um romaneio de compra selecionado, embute ref na observação
      const obs = cascataRomaneioSelecionado
        ? `Abatimento de compra #${cascataRomaneioSelecionado.numero_pedido}`
        : 'Pagamento em cascata';

      const resp = await axios.post(`${API}/sellers/${selectedSeller.id}/pagamento-cascata`, {
        valor_total: valor,
        data_pagamento: cascataData,
        forma_pagamento: cascataForma,
        observacao: obs,
        romaneio_origem_id: cascataRomaneioSelecionado?.id || null,
        romaneio_origem_numero: cascataRomaneioSelecionado?.numero_pedido || null
      });
      const d = resp.data;
      toast.success(
        `Aplicado R$ ${d.valor_aplicado.toFixed(2)} em ${d.romaneios_atualizados} romaneios` +
        (d.romaneios_quitados > 0 ? ` (${d.romaneios_quitados} quitados)` : '') +
        (d.valor_nao_aplicado > 0 ? ` • Saldo devedor +R$ ${d.valor_nao_aplicado.toFixed(2)}` : '')
      );
      setCascataValor('');
      setCascataRomaneioSelecionado(null);
      await fetchSellerRomaneios(selectedSeller);
      await refreshSelectedSeller();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao aplicar pagamento');
    } finally {
      setCascataLoading(false);
    }
  };
  
  const [formData, setFormData] = useState({
    nome: '',
    endereco: '',
    whatsapp: '',
    telefone: '',
    email: '',
    conta_bancaria: '',
    chave_pix: '',
    percentual_comissao: ''
  });

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/sellers`);
      const sellersData = response.data;
      
      const romResponse = await axios.get(`${API}/romaneios`);
      const romaneios = romResponse.data;
      
      const sellersComComissao = sellersData.map(seller => {
        const romaneiosVendedor = romaneios.filter(r => 
          r.vendedor_id === seller.id || r.vendedor_nome === seller.nome
        );
        // Romaneios onde o vendedor é CLIENTE (compras dele)
        const romaneiosComoCli = romaneios.filter(r =>
          r.cliente_id === seller.id ||
          (r.cliente_nome && seller.nome && r.cliente_nome.toLowerCase().trim() === seller.nome.toLowerCase().trim())
        );
        // Total de compras do vendedor (todas) e em aberto (não pagas)
        const totalComprasTodas = romaneiosComoCli.reduce((s, r) => s + (r.valor_total || 0), 0);
        const totalComprasEmAberto = romaneiosComoCli
          .filter(r => r.status_pagamento !== 'pago' && (r.forma_pagamento || '').toLowerCase() !== 'pago')
          .reduce((s, r) => s + (r.valor_total || 0) - (r.valor_pago || 0), 0);
        const qtdComprasEmAberto = romaneiosComoCli.filter(r => r.status_pagamento !== 'pago' && (r.forma_pagamento || '').toLowerCase() !== 'pago').length;
        
        let totalComissao = 0;
        let totalPago = 0;
        
        romaneiosVendedor.forEach(rom => {
          // Calcular comissão considerando % por item ou % geral
          let comissaoRom = 0;
          if (rom.items && rom.items.length > 0) {
            rom.items.forEach(item => {
              const comItem = item.percentual_comissao || seller.percentual_comissao || 0;
              comissaoRom += (item.valor_total || 0) * (comItem / 100);
            });
          } else {
            comissaoRom = (rom.valor_total || 0) * ((seller.percentual_comissao || 0) / 100);
          }
          totalComissao += comissaoRom;
          totalPago += rom.comissao_paga || 0;
        });
        
        return {
          ...seller,
          comissao_total_calculada: totalComissao,
          comissao_paga_calculada: totalPago,
          comissao_pendente_calculada: totalComissao - totalPago,
          total_vendas: romaneiosVendedor.reduce((acc, r) => acc + (r.valor_total || 0), 0),
          qtd_vendas: romaneiosVendedor.length,
          total_compras: totalComprasTodas,
          total_compras_em_aberto: totalComprasEmAberto,
          qtd_compras: romaneiosComoCli.length,
          qtd_compras_em_aberto: qtdComprasEmAberto,
        };
      });
      
      setSellers(sellersComComissao);
    } catch (error) {
      console.error('Erro ao carregar vendedores:', error);
      toast.error('Erro ao carregar vendedores');
    } finally {
      setLoading(false);
    }
  };

  // Buscar romaneios de um vendedor específico
  const fetchSellerRomaneios = async (seller) => {
    setLoadingRomaneios(true);
    try {
      const [romResponse, clienteResponse] = await Promise.all([
        axios.get(`${API}/romaneios`),
        axios.get(`${API}/vendedores/${seller.id}/romaneios-como-cliente`).catch(() => ({ data: [] }))
      ]);
      const romaneios = romResponse.data;
      
      // Romaneios onde ele VENDEU (para calcular comissão)
      const romaneiosVendedor = romaneios.filter(r => 
        r.vendedor_id === seller.id || r.vendedor_nome === seller.nome
      ).map(rom => {
        let comissaoCalculada = 0;
        if (rom.items && rom.items.length > 0) {
          rom.items.forEach(item => {
            const comItem = item.percentual_comissao || seller.percentual_comissao || 0;
            comissaoCalculada += (item.valor_total || 0) * (comItem / 100);
          });
        } else {
          comissaoCalculada = (rom.valor_total || 0) * ((seller.percentual_comissao || 0) / 100);
        }
        
        return {
          ...rom,
          comissao_calculada: comissaoCalculada,
          comissao_paga: rom.comissao_paga || 0,
          comissao_pendente: comissaoCalculada - (rom.comissao_paga || 0),
          historico_comissoes: rom.historico_comissoes || []
        };
      });
      
      // Romaneios onde ele COMPROU (é cliente) - vem do backend
      const romaneiosCliente = (clienteResponse.data || []).map(rom => ({
        ...rom,
        valor_total: rom.valor_total || 0
      }));
      
      romaneiosVendedor.sort((a, b) => new Date(b.data_criacao || b.created_at) - new Date(a.data_criacao || a.created_at));
      romaneiosCliente.sort((a, b) => new Date(b.data_criacao || b.created_at) - new Date(a.data_criacao || a.created_at));
      
      setSellerRomaneios(romaneiosVendedor);
      setRomaneiosComoCliente(romaneiosCliente);
    } catch (error) {
      console.error('Erro ao carregar romaneios:', error);
      toast.error('Erro ao carregar romaneios do vendedor');
    } finally {
      setLoadingRomaneios(false);
    }
  };

  const handleViewSeller = async (seller) => {
    setSelectedSeller(seller);
    await fetchSellerRomaneios(seller);
    // Load saldo data
    try {
      const saldoResp = await axios.get(`${API}/sellers/${seller.id}/saldo`);
      setSelectedSeller(prev => ({
        ...prev,
        saldo_devedor_atual: saldoResp.data?.saldo_devedor || 0,
        historico_saldo: saldoResp.data?.historico_saldo || []
      }));
    } catch (error) {
      console.error('Falha ao carregar saldo do vendedor:', error);
    }
  };

  const handleBackToList = () => {
    setSelectedSeller(null);
    setSellerRomaneios([]);
    fetchSellers(); // Atualizar lista principal
  };

  // FASE 2: Conceder acesso ao painel do vendedor (cria login e envia senha)
  // Modal de Conceder Acesso (com 3 opções de senha)
  const [showConcederModal, setShowConcederModal] = useState(false);
  const [concederSeller, setConcederSeller] = useState(null);
  const [concederForm, setConcederForm] = useState({
    email: '',
    metodo: 'auto_email', // 'manual' | 'auto_copiar' | 'auto_email'
    senha_personalizada: '',
    showPassword: false,
  });
  const [concederLoading, setConcederLoading] = useState(false);
  const [concederResult, setConcederResult] = useState(null); // { email, senha, email_enviado, action, erro_email }

  const openConcederModal = (seller) => {
    setConcederSeller(seller);
    setConcederForm({
      email: seller.email || '',
      metodo: 'auto_email',
      senha_personalizada: '',
      showPassword: false,
    });
    setConcederResult(null);
    setShowConcederModal(true);
  };

  const closeConcederModal = () => {
    setShowConcederModal(false);
    setConcederSeller(null);
    setConcederResult(null);
    setConcederForm({ email: '', metodo: 'auto_email', senha_personalizada: '', showPassword: false });
  };

  const submitConcederAcesso = async () => {
    if (!concederSeller) return;
    const email = (concederForm.email || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('E-mail inválido');
      return;
    }
    if (concederForm.metodo === 'manual') {
      if (!concederForm.senha_personalizada || concederForm.senha_personalizada.length < 4) {
        toast.error('Senha deve ter ao menos 4 caracteres');
        return;
      }
    }
    setConcederLoading(true);
    try {
      const payload = {
        email,
        name: concederSeller.nome,
        role: 'vendedor',
        seller_id: concederSeller.id,
        send_email: concederForm.metodo === 'auto_email',
        custom_password: concederForm.metodo === 'manual' ? concederForm.senha_personalizada : null,
        force_password_change: concederForm.metodo !== 'manual', // se admin define manualmente, não força troca
      };
      const resp = await axios.post(`${API}/auth/users/invite`, payload);
      const data = resp.data || {};
      setConcederResult({
        email: data.email,
        senha: data.senha_temporaria,
        email_enviado: data.email_enviado,
        action: data.action,
        erro_email: data.erro_email,
        must_change_password: data.must_change_password,
        metodo: concederForm.metodo,
      });
      // Salvar email no seller também (não-bloqueante)
      try {
        await axios.put(`${API}/sellers/${concederSeller.id}`, { ...concederSeller, email });
      } catch (e) { /* ignore */ }
      toast.success(`Acesso ${data.action} para ${concederSeller.nome}`);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Erro ao conceder acesso');
    } finally {
      setConcederLoading(false);
    }
  };

  const handleConcederAcesso = (seller) => openConcederModal(seller);

  const copiarCredenciais = async (formato = 'completo') => {
    if (!concederResult) return;
    const loginUrl = `${window.location.origin}/vendedor/login`;
    let texto = '';
    if (formato === 'senha') {
      texto = concederResult.senha;
    } else {
      texto = `Acesso ao Painel da Distribuidora Santa Ana\n\nE-mail: ${concederResult.email}\nSenha: ${concederResult.senha}\nLink: ${loginUrl}`;
      if (concederResult.must_change_password) {
        texto += `\n\nNo primeiro login será solicitado para criar uma nova senha pessoal.`;
      }
    }
    try {
      await navigator.clipboard.writeText(texto);
      toast.success(formato === 'senha' ? 'Senha copiada!' : 'Credenciais copiadas!');
    } catch (e) {
      toast.error('Não foi possível copiar — copie manualmente');
    }
  };

  // Alias para compat com chamadas legadas
  const fetchSellerData = fetchSellerRomaneios;

  // Mini-modal de edição inline do romaneio (para não sair da página do vendedor)
  const [editingRomaneioInline, setEditingRomaneioInline] = useState(null);
  const [editFormInline, setEditFormInline] = useState({ cliente_nome: '', valor_total: '', observacoes: '', forma_pagamento: 'a_receber', numero_nota_fiscal: '' });

  // Editar romaneio de compra (abre página de romaneios com modal aberto)
  const handleEditarRomaneioCompra = (rom) => {
    setEditingRomaneioInline(rom);
    setEditFormInline({
      cliente_nome: rom.cliente_nome || '',
      valor_total: String(rom.valor_total || ''),
      observacoes: rom.observacoes || '',
      forma_pagamento: rom.forma_pagamento || 'a_receber',
      numero_nota_fiscal: rom.numero_nota_fiscal || ''
    });
  };

  const submitEditRomaneioInline = async () => {
    if (!editingRomaneioInline) return;
    try {
      await axios.put(`${API}/romaneios/${editingRomaneioInline.id}`, {
        ...editingRomaneioInline,
        cliente_nome: editFormInline.cliente_nome,
        valor_total: parseFloat(editFormInline.valor_total) || 0,
        observacoes: editFormInline.observacoes,
        forma_pagamento: editFormInline.forma_pagamento,
        numero_nota_fiscal: editFormInline.numero_nota_fiscal,
      });
      toast.success(`Romaneio #${editingRomaneioInline.numero_pedido} atualizado!`);
      setEditingRomaneioInline(null);
      await fetchSellerRomaneios(selectedSeller);
    } catch (error) {
      console.error('Erro ao editar:', error);
      toast.error('Erro ao salvar alterações');
    }
  };

  // Marcar romaneio de compra como pago rapidamente
  const handleMarcarRomaneioPago = async (rom) => {
    if (!window.confirm(`Marcar o romaneio #${rom.numero_pedido} (${formatCurrency(rom.valor_total || 0)}) como PAGO?`)) return;
    try {
      await axios.put(`${API}/romaneios/${rom.id}`, {
        ...rom,
        status_pagamento: 'pago',
        forma_pagamento: 'pago',
        data_pagamento: new Date().toISOString()
      });
      toast.success(`Romaneio #${rom.numero_pedido} marcado como pago!`);
      await fetchSellerRomaneios(selectedSeller);
    } catch (error) {
      console.error('Erro ao marcar como pago:', error);
      toast.error('Erro ao marcar romaneio como pago');
    }
  };

  // Abrir modal de detalhes/edição de comissão do romaneio
  const handleOpenComissaoModal = (romaneio) => {
    setSelectedRomaneio(romaneio);
    setComissaoEditando({
      items: (romaneio.items || []).map(item => ({
        ...item,
        percentual_comissao_edit: item.percentual_comissao || selectedSeller?.percentual_comissao || 0
      }))
    });
    setShowComissaoModal(true);
  };

  // Salvar alterações de comissão por item
  const handleSaveComissaoItems = async () => {
    if (!selectedRomaneio || !comissaoEditando) return;
    
    try {
      // Preparar items com novos percentuais de comissão
      const itemsAtualizados = comissaoEditando.items.map(item => ({
        ...item,
        percentual_comissao: parseFloat(item.percentual_comissao_edit) || 0,
        comissao_percent: parseFloat(item.percentual_comissao_edit) || 0
      }));
      
      // Atualizar apenas os items do romaneio
      await axios.put(`${API}/romaneios/${selectedRomaneio.id}/items`, itemsAtualizados);
      
      toast.success('Comissões atualizadas com sucesso!');
      setShowComissaoModal(false);
      await fetchSellerRomaneios(selectedSeller);
      await refreshSelectedSeller();
    } catch (error) {
      console.error('Erro ao salvar comissões:', error);
      toast.error('Erro ao salvar comissões');
    }
  };

  // Abrir modal de pagamento de comissão
  const handleOpenPagamentoModal = (romaneio) => {
    setSelectedRomaneio(romaneio);
    setPagamentoData({
      valor: '',
      data: new Date().toISOString().split('T')[0],
      forma_pagamento: 'pix',
      observacao: ''
    });
    setAbatimentos([{ valor: '', descricao: '' }]);
    setShowPagamentoModal(true);
  };

  // Adicionar novo abatimento
  const addAbatimento = () => {
    setAbatimentos([...abatimentos, { valor: '', descricao: '' }]);
  };

  // Adicionar abatimento a partir de romaneio (vendedor comprou da empresa)
  const addAbatimentoFromRomaneio = () => {
    setShowRomaneioAbatimento(true);
  };

  // Remover abatimento
  const removeAbatimento = (index) => {
    if (abatimentos.length > 1) {
      setAbatimentos(abatimentos.filter((_, i) => i !== index));
    }
  };

  // Atualizar abatimento
  const updateAbatimento = (index, field, value) => {
    const newAbatimentos = [...abatimentos];
    newAbatimentos[index][field] = value;
    setAbatimentos(newAbatimentos);
  };

  // Calcular total de abatimentos
  const totalAbatimentos = abatimentos.reduce((sum, a) => sum + (parseFloat(String(a.valor).replace(',', '.')) || 0), 0);

  // Registrar pagamento de comissão
  const handleRegistrarPagamento = async () => {
    const valorStr = String(pagamentoData.valor || '0').replace(',', '.');
    const valorBruto = parseFloat(valorStr) || 0;
    
    if (valorBruto <= 0) {
      toast.error('Digite um valor válido maior que zero');
      return;
    }
    
    if (!pagamentoData.data) {
      toast.error('Selecione uma data');
      return;
    }
    
    // Calcular valor líquido (valor - abatimentos)
    const valorLiquido = Math.max(0, valorBruto - totalAbatimentos);
    
    // Montar observação com abatimentos se houver
    let observacaoFinal = pagamentoData.observacao || '';
    const abatimentosValidos = abatimentos.filter(a => parseFloat(String(a.valor).replace(',', '.')) > 0);
    
    if (abatimentosValidos.length > 0) {
      const abatimentosInfo = abatimentosValidos.map(a => 
        `R$ ${parseFloat(String(a.valor).replace(',', '.')).toFixed(2)}${a.descricao ? ` (${a.descricao})` : ''}`
      ).join(', ');
      const abatimentoTexto = `[Abatimentos: ${abatimentosInfo} = Total: R$ ${totalAbatimentos.toFixed(2)}]`;
      observacaoFinal = observacaoFinal ? `${observacaoFinal} | ${abatimentoTexto}` : abatimentoTexto;
    }
    
    try {
      // Preparar abatimentos estruturados (com romaneio_id e numero_pedido para histórico)
      const abatimentosPayload = abatimentosValidos
        .filter(a => a.romaneio_id)
        .map(a => {
          const valor = parseFloat(String(a.valor).replace(',', '.'));
          const romCompra = romaneiosComoCliente.find(r => r.id === a.romaneio_id);
          return {
            romaneio_id: a.romaneio_id,
            numero_pedido: romCompra?.numero_pedido || '',
            valor,
            descricao: a.descricao || `Abatimento Romaneio #${romCompra?.numero_pedido || '?'}`
          };
        });
      
      // Registrar no vendedor (saldo agregado)
      for (const ab of abatimentosPayload) {
        await axios.post(`${API}/vendedores/${selectedSeller.id}/abater-romaneio`, {
          romaneio_id: ab.romaneio_id,
          valor: ab.valor
        });
      }
      
      // Registrar pagamento (com abatimentos estruturados no histórico do romaneio de venda)
      if (valorLiquido > 0 || abatimentosPayload.length > 0) {
        await axios.post(`${API}/romaneios/${selectedRomaneio.id}/registrar-comissao`, {
          valor_pago: valorLiquido > 0 ? valorLiquido : 0,
          data_pagamento: pagamentoData.data,
          forma_pagamento: valorLiquido > 0 ? pagamentoData.forma_pagamento : 'abatimento',
          observacao: pagamentoData.observacao || (abatimentosPayload.length > 0 ? `Abatimento de ${abatimentosPayload.length} romaneio(s) de compra` : null),
          abatimentos: abatimentosPayload.length > 0 ? abatimentosPayload : null
        });
      }
      
      toast.success(`Pagamento registrado! ${totalAbatimentos > 0 ? `Abatimentos: ${formatCurrency(totalAbatimentos)}` : ''} ${valorLiquido > 0 ? `Líquido: ${formatCurrency(valorLiquido)}` : ''}`);
      setShowPagamentoModal(false);
      await fetchSellerRomaneios(selectedSeller);
      await refreshSelectedSeller();
    } catch (error) {
      console.error('Erro ao registrar pagamento:', error);
      toast.error(error.response?.data?.detail || 'Erro ao registrar pagamento');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        percentual_comissao: parseFloat(formData.percentual_comissao) || 0
      };
      
      if (editingSeller) {
        await axios.put(`${API}/sellers/${editingSeller.id}`, data);
        toast.success('Vendedor atualizado com sucesso!');
      } else {
        await axios.post(`${API}/sellers`, data);
        toast.success('Vendedor criado com sucesso!');
      }
      fetchSellers();
      closeModal();
    } catch (error) {
      toast.error('Erro ao salvar vendedor');
    }
  };

  const openNewModal = () => {
    setEditingSeller(null);
    setFormData({
      nome: '',
      endereco: '',
      whatsapp: '',
      telefone: '',
      email: '',
      conta_bancaria: '',
      chave_pix: '',
      percentual_comissao: ''
    });
    setShowModal(true);
  };

  const openEditModal = (seller) => {
    setEditingSeller(seller);
    setFormData({
      nome: seller.nome || '',
      endereco: seller.endereco || '',
      whatsapp: seller.whatsapp || '',
      telefone: seller.telefone || '',
      email: seller.email || '',
      conta_bancaria: seller.conta_bancaria || '',
      chave_pix: seller.chave_pix || '',
      percentual_comissao: seller.percentual_comissao?.toString() || ''
    });
    setShowModal(true);
  };

  const handleGenerateLink = async (seller) => {
    // Não pergunta mais o valor mínimo — o backend usa SEMPRE o valor global
    // configurado pelo admin em Configurações → Pedido Mínimo.
    try {
      const res = await axios.post(`${API}/seller-links`, {
        seller_id: seller.id,
        seller_name: seller.nome,
      });
      const link = `${window.location.origin}/pedido/${res.data.token}`;
      await navigator.clipboard.writeText(link);
      const vm = Number(res.data.valor_minimo) || 0;
      const msg = vm > 0
        ? `Link copiado! Pedido mínimo global: R$ ${vm.toFixed(2)}. Envie para ${seller.nome}`
        : `Link copiado! Envie para ${seller.nome}`;
      toast.success(msg, { duration: 6000 });
    } catch (error) {
      toast.error('Erro ao gerar link');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSeller(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar este vendedor?')) {
      try {
        await axios.delete(`${API}/sellers/${id}`);
        toast.success('Vendedor deletado com sucesso!');
        fetchSellers();
      } catch (error) {
        toast.error('Erro ao deletar vendedor');
      }
    }
  };

  // Funções de seleção múltipla
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
    } else {
      setSelectedIds(sellers.map(s => s.id));
      setSelectAll(true);
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(prev => prev.filter(i => i !== id));
      setSelectAll(false);
    } else {
      setSelectedIds(prev => [...prev, id]);
      if (selectedIds.length + 1 === sellers.length) setSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return toast.error('Selecione pelo menos um vendedor');
    if (!window.confirm(`Excluir ${selectedIds.length} vendedor(es)?`)) return;

    try {
      for (const id of selectedIds) await axios.delete(`${API}/sellers/${id}`);
      toast.success(`${selectedIds.length} vendedor(es) excluído(s)!`);
      setSelectedIds([]);
      setSelectAll(false);
      fetchSellers();
    } catch (error) {
      toast.error('Erro ao excluir vendedores');
    }
  };

  const handleExportCSV = () => {
    if (selectedIds.length === 0) return toast.error('Selecione pelo menos um vendedor');
    const selected = sellers.filter(s => selectedIds.includes(s.id));
    const headers = ['Nome', 'WhatsApp', 'Email', '% Comissão', 'Pendente', 'Pago'];
    const rows = selected.map(s => [s.nome, s.whatsapp || '', s.email || '', s.percentual_comissao || 0, (s.comissao_pendente_calculada || 0).toFixed(2), (s.comissao_paga_calculada || 0).toFixed(2)]);
    const csv = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'vendedores_exportados.csv';
    link.click();
    toast.success('Arquivo exportado!');
  };

  const clearSelection = () => { setSelectedIds([]); setSelectAll(false); };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  // ===================== VISUALIZAÇÃO DETALHADA DO VENDEDOR =====================
  if (selectedSeller) {
    return (
      <div className="space-y-6" data-testid="seller-detail-page">
        {/* Header com botão voltar */}
        <div className="flex items-center gap-4">
          <button 
            onClick={handleBackToList}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
            data-testid="back-to-sellers-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h2 className="text-2xl font-heading font-bold text-foreground flex items-center gap-3">
              {selectedSeller.nome}
              <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-normal">
                {selectedSeller.percentual_comissao || 0}% comissão
              </span>
            </h2>
            <p className="text-muted-foreground">{selectedSeller.whatsapp || selectedSeller.email || 'Sem contato'}</p>
          </div>
          <button
            onClick={async () => {
              const t = toast.loading('Gerando extrato PDF...');
              try {
                const resp = await axios.get(`${API}/sellers/${selectedSeller.id}/extrato/pdf`, { responseType: 'blob' });
                const url = URL.createObjectURL(resp.data);
                const win = window.open(url, '_blank');
                if (!win) {
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `extrato_${(selectedSeller.nome || 'vendedor').toLowerCase().replace(/\s/g, '_')}.pdf`;
                  a.click();
                }
                setTimeout(() => URL.revokeObjectURL(url), 5000);
                toast.success('Extrato pronto', { id: t });
              } catch (e) {
                toast.error('Erro ao gerar extrato', { id: t });
              }
            }}
            className="btn-secondary flex items-center gap-2"
            data-testid="download-extrato-pdf-btn"
            title="Baixar extrato completo em PDF"
          >
            <Download className="w-4 h-4" />
            Extrato PDF
          </button>
          <button 
            onClick={() => openEditModal(selectedSeller)}
            className="btn-secondary flex items-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Editar Vendedor
          </button>
        </div>

        {/* ===== PAINEL HERO DE RESUMO FINANCEIRO ===== */}
        {(() => {
          const emAberto = romaneiosComoCliente.filter(r => !(r.status_pagamento === 'pago' || (r.forma_pagamento || '').toLowerCase() === 'pago'));
          const totalCompras = emAberto.reduce((s, r) => s + (r.valor_total || 0), 0);
          const comissaoPendente = selectedSeller.comissao_pendente_calculada || 0;
          const comissaoPaga = selectedSeller.comissao_paga_calculada || 0;
          const comissaoTotal = selectedSeller.comissao_total_calculada || 0;
          // diff: + vendedor deve à loja, - loja deve ao vendedor
          const diff = totalCompras - comissaoPendente;
          const absDiff = Math.abs(diff);
          const podeCompensar = Math.min(totalCompras, comissaoPendente) > 0.01;
          
          let titulo, subtitulo, cor, bgGrad, icone;
          if (diff > 0.01) {
            titulo = `${selectedSeller.nome} deve à loja`;
            subtitulo = `Após abater toda comissão pendente, ainda restará ${formatCurrency(absDiff)} a pagar`;
            cor = 'red';
            bgGrad = 'from-red-500 to-rose-600';
            icone = '🔴';
          } else if (diff < -0.01) {
            titulo = `Loja deve ao vendedor`;
            subtitulo = `Após abater todas as compras, ainda restará ${formatCurrency(absDiff)} de comissão a pagar`;
            cor = 'amber';
            bgGrad = 'from-amber-500 to-orange-600';
            icone = '🟡';
          } else if (comissaoTotal === 0 && totalCompras === 0) {
            titulo = 'Sem movimentações';
            subtitulo = 'Este vendedor ainda não tem comissões nem compras registradas';
            cor = 'gray';
            bgGrad = 'from-slate-500 to-slate-600';
            icone = '⚪';
          } else {
            titulo = 'Conta zerada';
            subtitulo = 'Comissão pendente e compras em aberto estão equilibradas';
            cor = 'green';
            bgGrad = 'from-green-500 to-emerald-600';
            icone = '✅';
          }
          
          return (
            <div className={`rounded-xl overflow-hidden shadow-lg border-2 border-${cor}-300 dark:border-${cor}-800`} data-testid="hero-financeiro">
              {/* Banner principal */}
              <div className={`bg-gradient-to-r ${bgGrad} px-6 py-5 text-white`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-2xl mb-1">{icone}</div>
                    <p className="text-sm opacity-90">{titulo}</p>
                    <p className="text-4xl font-bold tracking-tight mt-1">
                      {(diff === 0 && comissaoTotal === 0 && totalCompras === 0) ? '—' : formatCurrency(absDiff)}
                    </p>
                    <p className="text-xs opacity-80 mt-1">{subtitulo}</p>
                  </div>
                  {podeCompensar && (
                    <button
                      onClick={() => {
                        setShowSelecionarRomaneioModal(true);
                      }}
                      className="bg-white/20 backdrop-blur-sm hover:bg-white/30 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors"
                      data-testid="btn-compensar-comissao"
                    >
                      <ShoppingBag className="w-4 h-4" />
                      Abater comissão em compras
                    </button>
                  )}
                </div>
              </div>

              {/* Quebra detalhada */}
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border bg-card">
                <div className="px-5 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Percent className="w-4 h-4 text-orange-600" />
                    <p className="text-xs text-muted-foreground">Comissão pendente (loja deve)</p>
                  </div>
                  <p className="text-xl font-bold text-orange-700 dark:text-orange-400">{formatCurrency(comissaoPendente)}</p>
                  <p className="text-[11px] text-muted-foreground">Total bruto: {formatCurrency(comissaoTotal)} · Já pago: {formatCurrency(comissaoPaga)}</p>
                </div>
                <div className="px-5 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <ShoppingBag className="w-4 h-4 text-purple-600" />
                    <p className="text-xs text-muted-foreground">Compras em aberto (vendedor deve)</p>
                  </div>
                  <p className="text-xl font-bold text-purple-700 dark:text-purple-400">{formatCurrency(totalCompras)}</p>
                  <p className="text-[11px] text-muted-foreground">{emAberto.length} de {romaneiosComoCliente.length} romaneios em aberto</p>
                </div>
                <div className="px-5 py-3">
                  <div className="flex items-center gap-2 mb-1">
                    <DollarSign className={`w-4 h-4 text-${cor}-600`} />
                    <p className="text-xs text-muted-foreground">Saldo final</p>
                  </div>
                  <p className={`text-xl font-bold text-${cor}-700 dark:text-${cor}-400`}>
                    {diff > 0.01 ? `+${formatCurrency(absDiff)}` : diff < -0.01 ? `−${formatCurrency(absDiff)}` : 'R$ 0,00'}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {diff > 0.01 ? 'A pagar para a loja' : diff < -0.01 ? 'A receber da loja' : 'Equilibrado'}
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Cards de Resumo - Compactos */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="seller-summary-cards">
          <div className="bg-card border border-border rounded-lg p-2.5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-md flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-muted-foreground leading-tight">Total Vendas</p>
                <p className="text-base font-bold leading-tight truncate">{formatCurrency(selectedSeller.total_vendas)}</p>
                <p className="text-[10px] text-muted-foreground">{selectedSeller.qtd_vendas || 0} romaneios</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card border border-border rounded-lg p-2.5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-md flex items-center justify-center flex-shrink-0">
                <Percent className="w-4 h-4 text-orange-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-muted-foreground leading-tight" title="Comissão total bruta">Comissão</p>
                <p className="text-base font-bold text-orange-600 leading-tight truncate">{formatCurrency(selectedSeller.comissao_total_calculada)}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card border border-border rounded-lg p-2.5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-md flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-muted-foreground leading-tight">Já Pago</p>
                <p className="text-base font-bold text-green-600 leading-tight truncate">{formatCurrency(selectedSeller.comissao_paga_calculada)}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card border border-border rounded-lg p-2.5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-md flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-4 h-4 text-red-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-muted-foreground leading-tight">Pendente</p>
                <p className="text-base font-bold text-red-600 leading-tight truncate">{formatCurrency(selectedSeller.comissao_pendente_calculada)}</p>
              </div>
            </div>
          </div>

          {/* Card novo: Total Compras (vendedor como cliente) — só em aberto */}
          {(() => {
            const emAberto = romaneiosComoCliente.filter(r => !(r.status_pagamento === 'pago' || (r.forma_pagamento || '').toLowerCase() === 'pago'));
            const totalAberto = emAberto.reduce((s, r) => s + (r.valor_total || 0), 0);
            return (
              <div className="bg-card border-2 border-purple-300 dark:border-purple-700 rounded-lg p-2.5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-md flex items-center justify-center flex-shrink-0">
                    <ShoppingBag className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] text-muted-foreground leading-tight">Compras em aberto</p>
                    <p className="text-base font-bold text-purple-700 leading-tight truncate">
                      {formatCurrency(totalAberto)}
                    </p>
                    <p className="text-[10px] text-muted-foreground">{emAberto.length} de {romaneiosComoCliente.length} romaneios</p>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Saldo Devedor do Vendedor (visível só se != 0) */}
        {(selectedSeller.saldo_devedor_atual && Math.abs(selectedSeller.saldo_devedor_atual) > 0.005) ? (
          <div className={`border-2 rounded-lg p-4 flex items-center justify-between flex-wrap gap-3 ${
            selectedSeller.saldo_devedor_atual > 0
              ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-700'
              : 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700'
          }`} data-testid="saldo-devedor-card">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                selectedSeller.saldo_devedor_atual > 0 ? 'bg-purple-200 dark:bg-purple-800' : 'bg-amber-200 dark:bg-amber-800'
              }`}>
                <AlertCircle className={`w-5 h-5 ${selectedSeller.saldo_devedor_atual > 0 ? 'text-purple-700' : 'text-amber-700'}`} />
              </div>
              <div>
                <p className="text-sm font-semibold">
                  {selectedSeller.saldo_devedor_atual > 0
                    ? `Vendedor deve à loja: ${formatCurrency(selectedSeller.saldo_devedor_atual)}`
                    : `Loja deve ao vendedor: ${formatCurrency(Math.abs(selectedSeller.saldo_devedor_atual))}`}
                </p>
                <p className="text-xs text-muted-foreground">
                  {selectedSeller.saldo_devedor_atual > 0
                    ? 'Próximas comissões serão automaticamente abatidas até zerar este saldo.'
                    : 'Este valor será abatido em pagamentos futuros.'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {selectedSeller.saldo_devedor_atual > 0 && (
                <button
                  onClick={async () => {
                    const input = window.prompt(`Quitar saldo devedor (vendedor pagou em dinheiro/pix). Valor (R$):`, selectedSeller.saldo_devedor_atual.toFixed(2).replace('.', ','));
                    if (!input) return;
                    const v = parseFloat(input.replace(',', '.'));
                    if (isNaN(v) || v <= 0) { toast.error('Valor inválido'); return; }
                    try {
                      await axios.post(`${API}/sellers/${selectedSeller.id}/saldo/quitar`, { valor: v });
                      toast.success('Pagamento registrado');
                      await fetchSellerRomaneios(selectedSeller);
                      await refreshSelectedSeller();
                    } catch (err) { toast.error('Erro ao quitar'); }
                  }}
                  className="btn-primary text-sm"
                  data-testid="quitar-saldo-btn"
                >
                  Receber pagamento
                </button>
              )}
              <button
                onClick={() => setShowHistoricoModal(true)}
                className="btn-secondary text-sm"
              >
                Ver histórico
              </button>
            </div>
          </div>
        ) : null}

        {/* Tabela de Romaneios do Vendedor */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-border bg-muted/50 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-semibold">Romaneios e Comissões</h3>
              <p className="text-sm text-muted-foreground">Gerencie as comissões de cada romaneio</p>
            </div>
            <button
              onClick={() => setShowHistoricoModal(true)}
              className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40"
              data-testid="ver-historico-btn"
            >
              <History className="w-3.5 h-3.5" />
              Ver histórico completo
            </button>
          </div>
          
          {loadingRomaneios ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : sellerRomaneios.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              Nenhum romaneio encontrado para este vendedor
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Nº Pedido</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Data</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Cliente</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Valor Venda</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Comissão</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-green-600">Pago</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-red-600">Pendente</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sellerRomaneios.map((rom) => {
                    const dataCriacao = rom.data_criacao || rom.created_at;
                    const dataFormatada = dataCriacao 
                      ? new Date(dataCriacao).toLocaleDateString('pt-BR')
                      : '-';
                    
                    // Detecta se algum pagamento veio via abatimento de compra (cascata)
                    const hist = rom.historico_comissoes || [];
                    const temPagamentoPorCompra = hist.some(h => h.tipo === 'cascata' || h.romaneio_origem_id || h.romaneio_origem_numero);
                    
                    let statusComissao = 'pendente';
                    let statusLabel = 'Pendente';
                    let statusClasses = 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
                    if (rom.comissao_pendente <= 0.01) {
                      // Totalmente pago
                      if (temPagamentoPorCompra) {
                        statusComissao = 'pago_compra';
                        statusLabel = 'Pago p/ Compra';
                        statusClasses = 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
                      } else {
                        statusComissao = 'pago';
                        statusLabel = 'Pago';
                        statusClasses = 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
                      }
                    } else if (rom.comissao_paga > 0) {
                      // Parcial
                      if (temPagamentoPorCompra) {
                        statusComissao = 'parcial_compra';
                        statusLabel = 'Parcial p/ Compra';
                        statusClasses = 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
                      } else {
                        statusComissao = 'parcial';
                        statusLabel = 'Parcial';
                        statusClasses = 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
                      }
                    }
                    
                    return (
                      <tr key={rom.id} className="hover:bg-accent/50 transition-colors">
                        <td className="px-4 py-3 font-medium">
                          #{rom.numero_pedido || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm">{dataFormatada}</td>
                        <td className="px-4 py-3 text-sm">{rom.cliente_nome || '-'}</td>
                        <td className="px-4 py-3 text-sm text-right font-medium">
                          {formatCurrency(rom.valor_total)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-orange-600">
                          {formatCurrency(rom.comissao_calculada)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-green-600">
                          {formatCurrency(rom.comissao_paga)}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-red-600">
                          {formatCurrency(rom.comissao_pendente)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${statusClasses}`}
                            data-testid={`status-comissao-${rom.numero_pedido}`}
                            title={
                              temPagamentoPorCompra
                                ? hist.filter(h => h.tipo === 'cascata' || h.romaneio_origem_id)
                                      .map(h => `Abatido R$ ${(h.valor||0).toFixed(2)} via Romaneio #${h.romaneio_origem_numero || '?'} em ${h.registrado_em ? new Date(h.registrado_em).toLocaleString('pt-BR') : (h.data || '-')}`)
                                      .join('\n')
                                : statusLabel
                            }
                          >
                            {statusLabel}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleOpenComissaoModal(rom)}
                              className="p-1.5 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded text-blue-600"
                              title="Ver/Editar Comissões por Item"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {rom.comissao_pendente > 0 ? (
                              <button
                                onClick={() => handleOpenPagamentoModal(rom)}
                                className="p-1.5 hover:bg-green-100 dark:hover:bg-green-900/30 rounded text-green-600"
                                title="Registrar Pagamento"
                              >
                                <DollarSign className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => { setSelectedRomaneio(rom); setShowComissaoModal(true); }}
                                className="p-1.5 hover:bg-green-100 dark:hover:bg-green-900/30 rounded text-green-600 opacity-70"
                                title="Editar Pagamentos (já pago)"
                              >
                                <DollarSign className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {/* Totais */}
                <tfoot className="bg-muted/50 border-t border-border">
                  <tr>
                    <td colSpan="3" className="px-4 py-3 text-sm font-bold text-right">TOTAIS:</td>
                    <td className="px-4 py-3 text-sm text-right font-bold">
                      {formatCurrency(sellerRomaneios.reduce((acc, r) => acc + (r.valor_total || 0), 0))}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-orange-600">
                      {formatCurrency(sellerRomaneios.reduce((acc, r) => acc + (r.comissao_calculada || 0), 0))}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-green-600">
                      {formatCurrency(sellerRomaneios.reduce((acc, r) => acc + (r.comissao_paga || 0), 0))}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-red-600">
                      {formatCurrency(sellerRomaneios.reduce((acc, r) => acc + (r.comissao_pendente || 0), 0))}
                    </td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Compras do Vendedor (onde vendedor é cliente) — Sempre visível */}
        <div className="bg-card border-2 border-purple-500 rounded-lg overflow-hidden" data-testid="compras-vendedor-section">
          {(() => {
            const emAberto = romaneiosComoCliente.filter(r => !(r.status_pagamento === 'pago' || (r.forma_pagamento || '').toLowerCase() === 'pago'));
            const totalAberto = emAberto.reduce((s, r) => s + (r.valor_total || 0), 0);
            const totalGeral = romaneiosComoCliente.reduce((s, r) => s + (r.valor_total || 0), 0);
            return (
              <div className="px-4 py-3 border-b border-purple-300 bg-purple-100 dark:bg-purple-900/30 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-bold text-lg flex items-center gap-2 text-purple-700 dark:text-purple-300">
                    <FileText className="w-5 h-5" />
                    Romaneios de Compra ({emAberto.length}/{romaneiosComoCliente.length})
                  </h3>
                  <p className="text-sm text-muted-foreground">Romaneios em que <strong>{selectedSeller.nome}</strong> comprou da empresa (como cliente)</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Em aberto</p>
                  <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                    {formatCurrency(totalAberto)}
                  </p>
                  <p className="text-[11px] text-muted-foreground">Total geral: {formatCurrency(totalGeral)}</p>
                </div>
              </div>
            );
          })()}
          {romaneiosComoCliente.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <p className="text-sm">Nenhum romaneio de compra encontrado para <strong>{selectedSeller.nome}</strong>.</p>
              <p className="text-xs mt-1">Se ele comprou da empresa, certifique-se que o nome do cliente no romaneio é exatamente "{selectedSeller.nome}".</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-3 py-2 text-left">Nº Pedido</th>
                    <th className="px-3 py-2 text-left">Data</th>
                    <th className="px-3 py-2 text-right">Valor</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Como foi quitado / Histórico</th>
                    <th className="px-3 py-2 text-center w-32">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {romaneiosComoCliente.map(rom => {
                    const usosNoSaldo = (selectedSeller.historico_saldo || []).filter(h =>
                      h.romaneio_id === rom.id ||
                      (h.observacao || '').includes(`#${rom.numero_pedido}`) ||
                      h.romaneio_numero === rom.numero_pedido
                    );
                    // E também pagamentos de comissão que vieram dessa compra (cascata)
                    const usosNasComissoes = sellerRomaneios.flatMap(romC =>
                      (romC.historico_comissoes || []).filter(h =>
                        h.romaneio_origem_id === rom.id ||
                        h.romaneio_origem_numero === rom.numero_pedido
                      ).map(h => ({...h, romaneio_destino: romC.numero_pedido}))
                    );
                    const totalAbatidoComissoes = usosNasComissoes.reduce((s, h) => s + (h.valor || 0), 0);
                    const totalSaldo = usosNoSaldo.reduce((s, h) => s + Math.abs(h.valor || 0), 0);
                    const totalUtilizado = totalAbatidoComissoes + totalSaldo;

                    const valor = rom.valor_total || 0;
                    const pago = rom.status_pagamento === 'pago' || (rom.forma_pagamento || '').toLowerCase() === 'pago';
                    let status, statusColor;
                    if (pago) { status = '✓ Pago'; statusColor = 'green'; }
                    else if (totalUtilizado >= valor - 0.01) { status = '🔻 Abatido em comissão'; statusColor = 'purple'; }
                    else if (totalUtilizado > 0) { status = '↻ Parcialmente abatido'; statusColor = 'amber'; }
                    else { status = '⏰ A receber'; statusColor = 'yellow'; }

                    const colorMap = {
                      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                      purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
                      amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
                      yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                    };

                    return (
                      <tr key={rom.id} className="hover:bg-accent/30">
                        <td className="px-3 py-2 font-medium">#{rom.numero_pedido}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                          {rom.data_criacao ? new Date(rom.data_criacao).toLocaleDateString('pt-BR') :
                           rom.created_at ? new Date(rom.created_at).toLocaleDateString('pt-BR') : '-'}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold">{formatCurrency(valor)}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs ${colorMap[statusColor]} px-2 py-0.5 rounded whitespace-nowrap`}>{status}</span>
                        </td>
                        <td className="px-3 py-2">
                          {(usosNasComissoes.length === 0 && usosNoSaldo.length === 0) ? (
                            <span className="text-xs text-muted-foreground">—</span>
                          ) : (
                            <div className="space-y-0.5 text-xs">
                              {usosNasComissoes.map((h, i) => (
                                <div key={`c${i}`} className="text-purple-700 dark:text-purple-400">
                                  → Abateu R$ {h.valor.toFixed(2)} na comissão #{h.romaneio_destino}
                                </div>
                              ))}
                              {usosNoSaldo.map((h, i) => (
                                <div key={`s${i}`} className="text-muted-foreground">
                                  {h.tipo === 'abatimento_excedente' ? '↑ Excedente: ' : h.tipo === 'auto_offset' ? '⚙️ Auto-abatimento: ' : '• '}
                                  R$ {Math.abs(h.valor).toFixed(2)} ({h.data ? new Date(h.data).toLocaleDateString('pt-BR') : '-'})
                                </div>
                              ))}
                              {totalUtilizado < valor - 0.01 && !pago && (
                                <div className="text-amber-600 font-medium">
                                  Restam {formatCurrency(valor - totalUtilizado)} a quitar
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleEditarRomaneioCompra(rom)}
                              className="p-1.5 hover:bg-primary/10 text-primary rounded transition-colors"
                              title="Editar romaneio completo"
                              data-testid={`btn-editar-romaneio-compra-${rom.numero_pedido}`}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            {!pago && (
                              <button
                                onClick={() => handleMarcarRomaneioPago(rom)}
                                className="p-1.5 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 rounded transition-colors"
                                title="Marcar como pago"
                                data-testid={`btn-marcar-pago-${rom.numero_pedido}`}
                              >
                                <DollarSign className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-purple-50 dark:bg-purple-900/20 border-t-2 border-purple-300">
                  {(() => {
                    const emAberto = romaneiosComoCliente.filter(r => !(r.status_pagamento === 'pago' || (r.forma_pagamento || '').toLowerCase() === 'pago'));
                    const totalAberto = emAberto.reduce((s, r) => s + (r.valor_total || 0), 0);
                    const totalGeral = romaneiosComoCliente.reduce((s, r) => s + (r.valor_total || 0), 0);
                    const totalPago = totalGeral - totalAberto;
                    return (
                      <>
                        <tr>
                          <td colSpan="2" className="px-3 py-2 text-right font-bold">EM ABERTO:</td>
                          <td className="px-3 py-2 text-right font-bold text-purple-700">{formatCurrency(totalAberto)}</td>
                          <td colSpan="3" className="px-3 py-2 text-xs text-muted-foreground">{emAberto.length} romaneio(s) não pago(s)</td>
                        </tr>
                        {totalPago > 0 && (
                          <tr className="text-muted-foreground">
                            <td colSpan="2" className="px-3 py-1 text-right text-xs">Já pago (não contabilizado):</td>
                            <td className="px-3 py-1 text-right text-xs line-through">{formatCurrency(totalPago)}</td>
                            <td colSpan="3"></td>
                          </tr>
                        )}
                      </>
                    );
                  })()}
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Modal de Seleção de Romaneio para Abater */}
        {showSelecionarRomaneioModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onClick={() => setShowSelecionarRomaneioModal(false)}
            data-testid="selecionar-romaneio-modal"
          >
            <div
              className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}
            >
              <div className="px-5 py-3 border-b border-border bg-muted/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  <div>
                    <h3 className="font-semibold">Selecionar Romaneio para Abater</h3>
                    <p className="text-xs text-muted-foreground">Escolha um romaneio cujo valor será deduzido da comissão de {selectedSeller?.nome}</p>
                  </div>
                </div>
                <button onClick={() => setShowSelecionarRomaneioModal(false)} className="p-1.5 hover:bg-accent rounded">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-3 border-b border-border">
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg px-3 py-2 mb-2 text-xs text-purple-900 dark:text-purple-100">
                  Mostrando apenas <b>romaneios de compra em aberto</b> de <b>{selectedSeller?.nome}</b> (onde o vendedor é o cliente). Romaneios já pagos não aparecem.
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Buscar por nº pedido..."
                    value={searchRomaneio}
                    onChange={(e) => setSearchRomaneio(e.target.value)}
                    className="input-field pl-10"
                    autoFocus
                    data-testid="search-romaneio-input"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-auto">
                {(() => {
                  const term = searchRomaneio.toLowerCase().trim();
                  // Mostrar APENAS romaneios de compra do vendedor selecionado (onde ele é o cliente)
                  // e que ainda estão em aberto (não pagos)
                  const filtered = romaneiosComoCliente.filter(r => {
                    const pago = r.status_pagamento === 'pago' || (r.forma_pagamento || '').toLowerCase() === 'pago';
                    if (pago) return false;
                    if (!term) return true;
                    return (
                      String(r.numero_pedido || '').toLowerCase().includes(term) ||
                      String(r.cliente_nome || '').toLowerCase().includes(term)
                    );
                  }).sort((a, b) => String(b.data_criacao || b.created_at || '').localeCompare(String(a.data_criacao || a.created_at || '')));

                  if (romaneiosComoCliente.length === 0) {
                    return (
                      <div className="p-8 text-center text-muted-foreground">
                        <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
                        <p className="font-medium">Nenhum romaneio de compra encontrado</p>
                        <p className="text-xs mt-1">{selectedSeller?.nome} ainda não tem romaneios onde figura como cliente.</p>
                      </div>
                    );
                  }
                  if (filtered.length === 0) {
                    return <div className="p-8 text-center text-muted-foreground">Nenhum romaneio em aberto encontrado{term ? ' com esse filtro' : ''}.</div>;
                  }
                  return (
                    <table className="w-full text-sm">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Nº Pedido</th>
                          <th className="px-3 py-2 text-left">Data</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-right">Valor</th>
                          <th className="px-3 py-2 text-center w-20">Ação</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {filtered.slice(0, 100).map(rom => (
                          <tr key={rom.id} className="hover:bg-accent/30">
                            <td className="px-3 py-2 font-medium">#{rom.numero_pedido}</td>
                            <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                              {rom.data_criacao ? new Date(rom.data_criacao).toLocaleDateString('pt-BR') :
                               rom.created_at ? new Date(rom.created_at).toLocaleDateString('pt-BR') : '-'}
                            </td>
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                                Em aberto
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right font-semibold">{formatCurrency(rom.valor_total || 0)}</td>
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={async () => {
                                  // Aplica o abatimento direto: usa valor do romaneio como valor a abater
                                  setCascataRomaneioSelecionado(rom);
                                  const valor = rom.valor_total || 0;
                                  setCascataValor(valor.toFixed(2).replace('.', ','));
                                  setCascataForma('abatimento');
                                  setShowSelecionarRomaneioModal(false);
                                  setSearchRomaneio('');
                                  // Executa imediatamente
                                  setCascataLoading(true);
                                  try {
                                    const resp = await axios.post(`${API}/sellers/${selectedSeller.id}/pagamento-cascata`, {
                                      valor_total: valor,
                                      data_pagamento: cascataData,
                                      forma_pagamento: 'abatimento',
                                      observacao: `Pago via Romaneio de Compra #${rom.numero_pedido}`,
                                      romaneio_origem_id: rom.id,
                                      romaneio_origem_numero: rom.numero_pedido,
                                    });
                                    const data = resp.data || {};
                                    toast.success(`Abatido R$ ${valor.toFixed(2)} via Romaneio #${rom.numero_pedido} em ${data.romaneios_atualizados || 0} comissão(ões)`);
                                    // Recarregar dados e marcar o romaneio de compra como pago
                                    await fetchSellerData(selectedSeller);
                                    await fetchSellerRomaneios(selectedSeller);
                                    setCascataValor('');
                                    setCascataRomaneioSelecionado(null);
                                  } catch (err) {
                                    console.error(err);
                                    const msg = err?.response?.data?.detail || 'Erro ao aplicar abatimento';
                                    toast.error(msg);
                                  } finally {
                                    setCascataLoading(false);
                                  }
                                }}
                                className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg font-medium"
                                data-testid={`pick-romaneio-${rom.id}`}
                              >
                                Abater
                              </button>
                            </td>
                          </tr>
                        ))}
                        {filtered.length > 100 && (
                          <tr>
                            <td colSpan="5" className="px-3 py-2 text-center text-xs text-muted-foreground bg-muted/30">
                              Mostrando 100 de {filtered.length} resultados. Refine a busca.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Modal de Histórico Completo de Pagamentos do Vendedor */}
        {showHistoricoModal && selectedSeller && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            data-testid="historico-modal"
          >
            <div
              className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="px-6 py-4 border-b border-border bg-muted/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-600" />
                  <div>
                    <h3 className="text-lg font-semibold">Histórico Completo de Pagamentos</h3>
                    <p className="text-sm text-muted-foreground">{selectedSeller.nome} • Edite ou exclua qualquer pagamento</p>
                  </div>
                </div>
                <button onClick={() => setShowHistoricoModal(false)} className="p-2 hover:bg-accent rounded-lg" data-testid="historico-close-btn">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {(() => {
                // 1) Pagamentos de comissão em romaneios
                const pagComissao = sellerRomaneios.flatMap(rom =>
                  (rom.historico_comissoes || []).map((h, idx) => ({
                    tipo_evento: h.tipo === 'auto_offset' ? 'auto_offset' : (h.tipo === 'cascata' ? 'cascata' : 'pagamento'),
                    data: h.data,
                    registrado_em: h.registrado_em || h.data,
                    valor: h.valor || 0,
                    romaneio_id: rom.id,
                    numero_pedido: rom.numero_pedido,
                    cliente_nome: rom.cliente_nome,
                    forma_pagamento: h.forma_pagamento,
                    observacao: h.observacao,
                    romaneio_origem_id: h.romaneio_origem_id,
                    romaneio_origem_numero: h.romaneio_origem_numero,
                    pagamento_index: idx,
                    origem: 'romaneio',
                  }))
                );
                // 2) Entradas de saldo do vendedor (excedentes, quitações, auto-offsets)
                const saldoEntries = (selectedSeller.historico_saldo || []).map(h => ({
                  tipo_evento: h.tipo || 'saldo',
                  data: h.data,
                  valor: h.valor || 0,
                  romaneio_id: h.romaneio_id || null,
                  numero_pedido: h.romaneio_numero || null,
                  forma_pagamento: h.forma_pagamento,
                  observacao: h.observacao,
                  origem: 'saldo',
                  entry_id: h.id
                }));
                const historico = [...pagComissao, ...saldoEntries]
                  .sort((a, b) => String(b.data || '').localeCompare(String(a.data || '')));

                // Aplica filtros
                const filtrado = historico.filter(h => {
                  if (histFilters.dataInicio && String(h.data || '') < histFilters.dataInicio) return false;
                  if (histFilters.dataFim && String(h.data || '') > histFilters.dataFim) return false;
                  if (histFilters.romaneio) {
                    const q = histFilters.romaneio.toLowerCase();
                    if (!String(h.numero_pedido || '').toLowerCase().includes(q) &&
                        !String(h.romaneio_origem_numero || '').toLowerCase().includes(q) &&
                        !String(h.cliente_nome || '').toLowerCase().includes(q)) return false;
                  }
                  if (histFilters.valorMin && Math.abs(h.valor || 0) < parseFloat(histFilters.valorMin)) return false;
                  if (histFilters.valorMax && Math.abs(h.valor || 0) > parseFloat(histFilters.valorMax)) return false;
                  if (histFilters.tipo && h.tipo_evento !== histFilters.tipo) return false;
                  return true;
                });

                const keyOf = (h) => `${h.origem}-${h.romaneio_id || h.entry_id}-${h.pagamento_index ?? 0}-${h.data}`;
                const selectedKeys = Object.keys(histSelected).filter(k => histSelected[k]);
                const allSelected = filtrado.length > 0 && filtrado.every(h => histSelected[keyOf(h)]);

                const totalComissao = pagComissao.reduce((s, h) => s + (h.valor || 0), 0);
                const saldoAtual = selectedSeller.saldo_devedor_atual || 0;

                const handleDeleteSelected = async () => {
                  if (selectedKeys.length === 0) return;
                  if (!window.confirm(`Excluir ${selectedKeys.length} pagamento(s) selecionado(s)? Esta ação não pode ser desfeita.`)) return;
                  let ok = 0, fail = 0;
                  // ordenar selecionados por origem e ordem reversa do índice para não bagunçar índices
                  const itensSelecionados = filtrado.filter(h => histSelected[keyOf(h)]);
                  // agrupar por romaneio_id+origem
                  const porRom = {};
                  itensSelecionados.forEach(h => {
                    if (h.origem === 'romaneio') {
                      if (!porRom[h.romaneio_id]) porRom[h.romaneio_id] = [];
                      porRom[h.romaneio_id].push(h);
                    }
                  });
                  for (const romId of Object.keys(porRom)) {
                    const arr = porRom[romId].sort((a, b) => b.pagamento_index - a.pagamento_index);
                    for (const h of arr) {
                      try {
                        await axios.delete(`${API}/romaneios/${romId}/comissao/${h.pagamento_index}`);
                        ok++;
                      } catch (err) { fail++; console.error(err); }
                    }
                  }
                  setHistSelected({});
                  toast.success(`${ok} excluído(s)${fail ? ` · ${fail} falhou(am)` : ''}`);
                  await fetchSellerData(selectedSeller);
                  await fetchSellerRomaneios(selectedSeller);
                };

                return (
                  <>
                    <div className="px-6 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-border flex items-center justify-between flex-wrap gap-3">
                      <div className="text-sm space-x-4">
                        <span><span className="text-muted-foreground">Comissões pagas:</span> <strong className="text-green-600">{formatCurrency(totalComissao)}</strong></span>
                        {Math.abs(saldoAtual) > 0.005 && (
                          <span>
                            <span className="text-muted-foreground">Saldo devedor:</span>{' '}
                            <strong className={saldoAtual > 0 ? 'text-purple-600' : 'text-amber-600'}>
                              {formatCurrency(saldoAtual)}
                            </strong>
                          </span>
                        )}
                        <span><span className="text-muted-foreground">Mostrando:</span> <strong>{filtrado.length}</strong> de {historico.length}</span>
                      </div>
                      {selectedKeys.length > 0 && (
                        <button
                          onClick={handleDeleteSelected}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded text-xs font-semibold flex items-center gap-1.5"
                          data-testid="historico-delete-selected"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Excluir {selectedKeys.length} selecionado(s)
                        </button>
                      )}
                    </div>

                    {/* Barra de Filtros */}
                    <div className="px-6 py-3 border-b border-border bg-muted/30 grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
                      <div>
                        <label className="block text-[10px] text-muted-foreground mb-0.5">Data início</label>
                        <input type="date" value={histFilters.dataInicio} onChange={(e) => setHistFilters(prev => ({ ...prev, dataInicio: e.target.value }))} className="input-field text-xs h-8 w-full" data-testid="hist-filter-data-inicio" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-muted-foreground mb-0.5">Data fim</label>
                        <input type="date" value={histFilters.dataFim} onChange={(e) => setHistFilters(prev => ({ ...prev, dataFim: e.target.value }))} className="input-field text-xs h-8 w-full" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-muted-foreground mb-0.5">Romaneio / Cliente</label>
                        <input type="text" placeholder="ex: 0050 ou nome" value={histFilters.romaneio} onChange={(e) => setHistFilters(prev => ({ ...prev, romaneio: e.target.value }))} className="input-field text-xs h-8 w-full" data-testid="hist-filter-romaneio" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-muted-foreground mb-0.5">Valor mín</label>
                        <input type="number" step="0.01" placeholder="0" value={histFilters.valorMin} onChange={(e) => setHistFilters(prev => ({ ...prev, valorMin: e.target.value }))} className="input-field text-xs h-8 w-full" />
                      </div>
                      <div>
                        <label className="block text-[10px] text-muted-foreground mb-0.5">Valor máx</label>
                        <input type="number" step="0.01" placeholder="9999" value={histFilters.valorMax} onChange={(e) => setHistFilters(prev => ({ ...prev, valorMax: e.target.value }))} className="input-field text-xs h-8 w-full" />
                      </div>
                      <div className="flex gap-1">
                        <div className="flex-1">
                          <label className="block text-[10px] text-muted-foreground mb-0.5">Tipo</label>
                          <select value={histFilters.tipo} onChange={(e) => setHistFilters(prev => ({ ...prev, tipo: e.target.value }))} className="input-field text-xs h-8 w-full">
                            <option value="">Todos</option>
                            <option value="pagamento">Pagamento</option>
                            <option value="cascata">Pago p/ Compra</option>
                            <option value="auto_offset">Auto-abatimento</option>
                            <option value="abatimento_excedente">Dívida do vendedor</option>
                            <option value="pagamento_quitacao">Vendedor pagou</option>
                          </select>
                        </div>
                        {(histFilters.dataInicio || histFilters.dataFim || histFilters.romaneio || histFilters.valorMin || histFilters.valorMax || histFilters.tipo) && (
                          <button
                            onClick={() => setHistFilters({ dataInicio: '', dataFim: '', romaneio: '', valorMin: '', valorMax: '', tipo: '' })}
                            className="self-end px-2 h-8 rounded border border-border hover:bg-accent text-xs"
                            title="Limpar filtros"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="flex-1 overflow-auto">
                      {filtrado.length === 0 ? (
                        <div className="p-12 text-center text-muted-foreground">
                          {historico.length === 0 ? 'Nenhum pagamento registrado ainda para este vendedor.' : 'Nenhum lançamento encontrado com os filtros aplicados.'}
                        </div>
                      ) : (
                        <table className="w-full text-sm">
                          <thead className="bg-muted sticky top-0">
                            <tr>
                              <th className="px-3 py-2 text-center w-10">
                                <input
                                  type="checkbox"
                                  checked={allSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      const next = {};
                                      filtrado.forEach(h => { next[keyOf(h)] = true; });
                                      setHistSelected(next);
                                    } else {
                                      setHistSelected({});
                                    }
                                  }}
                                  className="w-4 h-4 accent-red-600"
                                  data-testid="hist-select-all"
                                />
                              </th>
                              <th className="px-3 py-2 text-left">Data / Hora</th>
                              <th className="px-3 py-2 text-left">Tipo</th>
                              <th className="px-3 py-2 text-left">Romaneio de Venda</th>
                              <th className="px-3 py-2 text-left">Romaneio de Compra abatido</th>
                              <th className="px-3 py-2 text-right">Valor</th>
                              <th className="px-3 py-2 text-left">Observação</th>
                              <th className="px-3 py-2 text-center w-24">Ações</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {filtrado.map((h, i) => {
                              const isSaldo = h.origem === 'saldo';
                              const isAutoOffset = h.tipo_evento === 'auto_offset';
                              const isExcedente = h.tipo_evento === 'abatimento_excedente';
                              const isCascata = h.tipo_evento === 'cascata';
                              const isQuitacao = h.tipo_evento === 'pagamento_quitacao';
                              const valorMostrar = isSaldo ? h.valor : h.valor;

                              // Data/hora exata
                              const dataLabel = h.registrado_em
                                ? new Date(h.registrado_em).toLocaleString('pt-BR')
                                : (h.data ? new Date(h.data).toLocaleDateString('pt-BR') : '-');

                              // Etiqueta tipo
                              let tipoBadge = null;
                              if (isAutoOffset) tipoBadge = <span className="text-xs bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 px-2 py-0.5 rounded whitespace-nowrap">⚙️ Auto-abatimento</span>;
                              else if (isExcedente) tipoBadge = <span className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-2 py-0.5 rounded whitespace-nowrap">➕ Dívida do vendedor</span>;
                              else if (isQuitacao) tipoBadge = <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 px-2 py-0.5 rounded whitespace-nowrap">💵 Vendedor pagou</span>;
                              else if (isCascata) tipoBadge = <span className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-2 py-0.5 rounded whitespace-nowrap">🛒 Pago p/ Compra</span>;
                              else if (h.forma_pagamento === 'abatimento') tipoBadge = <span className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-2 py-0.5 rounded whitespace-nowrap">🔻 Abatimento</span>;
                              else tipoBadge = <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded whitespace-nowrap capitalize">💰 {h.forma_pagamento || 'pagamento'}</span>;

                              return (
                              <tr key={`${h.origem}-${h.romaneio_id || h.entry_id}-${h.pagamento_index ?? i}`} className="hover:bg-accent/30" data-testid={`historico-row-${i}`}>
                                <td className="px-3 py-2 text-center">
                                  {h.origem === 'romaneio' && (
                                    <input
                                      type="checkbox"
                                      checked={!!histSelected[keyOf(h)]}
                                      onChange={(e) => setHistSelected(prev => ({ ...prev, [keyOf(h)]: e.target.checked }))}
                                      className="w-4 h-4 accent-red-600"
                                      data-testid={`hist-row-check-${i}`}
                                    />
                                  )}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-xs">{dataLabel}</td>
                                <td className="px-3 py-2">{tipoBadge}</td>
                                {/* Romaneio de venda (de onde a comissão foi gerada) */}
                                <td className="px-3 py-2">
                                  {h.numero_pedido ? (
                                    <div>
                                      <div className="font-medium">#{h.numero_pedido}</div>
                                      {h.cliente_nome && <div className="text-xs text-muted-foreground truncate max-w-[180px]">{h.cliente_nome}</div>}
                                    </div>
                                  ) : <span className="text-muted-foreground text-xs">—</span>}
                                </td>
                                {/* Romaneio de compra abatido (quando cascata) */}
                                <td className="px-3 py-2">
                                  {h.romaneio_origem_numero ? (
                                    <div className="text-purple-700 dark:text-purple-300">
                                      <div className="font-medium">#{h.romaneio_origem_numero}</div>
                                      <div className="text-[10px] text-muted-foreground">(romaneio que abateu)</div>
                                    </div>
                                  ) : isCascata ? (
                                    <span className="text-muted-foreground text-xs italic">romaneio não identificado</span>
                                  ) : (
                                    <span className="text-muted-foreground text-xs">—</span>
                                  )}
                                </td>
                                <td className={`px-3 py-2 text-right font-semibold ${valorMostrar >= 0 ? 'text-green-600' : 'text-amber-600'}`}>
                                  {valorMostrar >= 0 ? '' : '−'}{formatCurrency(Math.abs(valorMostrar))}
                                </td>
                                <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[200px]" title={h.observacao}>{h.observacao || '-'}</td>
                                <td className="px-3 py-2 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      data-testid={`historico-edit-${i}`}
                                      onClick={async () => {
                                        const valorAtual = Math.abs(h.valor || 0).toFixed(2).replace('.', ',');
                                        const input = window.prompt(`Editar valor do lançamento:`, valorAtual);
                                        if (input === null) return;
                                        const novoValorAbs = parseFloat(input.replace(',', '.'));
                                        if (isNaN(novoValorAbs) || novoValorAbs < 0) { toast.error('Valor inválido'); return; }
                                        try {
                                          if (isSaldo) {
                                            // manter sinal original (negativo se era negativo)
                                            const novoValor = (h.valor < 0 ? -novoValorAbs : novoValorAbs);
                                            await axios.put(`${API}/sellers/${selectedSeller.id}/saldo/${h.entry_id}`, { valor: novoValor });
                                          } else {
                                            await axios.put(`${API}/romaneios/${h.romaneio_id}/comissao/${h.pagamento_index}`, { valor: novoValorAbs });
                                          }
                                          toast.success('Lançamento atualizado');
                                          await fetchSellerRomaneios(selectedSeller);
                                          await refreshSelectedSeller();
                                        } catch { toast.error('Erro ao atualizar'); }
                                      }}
                                      className="text-blue-500 hover:text-blue-700 p-1"
                                      title="Editar valor"
                                    >
                                      <Edit className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      data-testid={`historico-delete-${i}`}
                                      onClick={async () => {
                                        if (!window.confirm(`Excluir este lançamento de ${formatCurrency(Math.abs(h.valor))}?`)) return;
                                        try {
                                          if (isSaldo) {
                                            await axios.delete(`${API}/sellers/${selectedSeller.id}/saldo/${h.entry_id}`);
                                          } else {
                                            await axios.delete(`${API}/romaneios/${h.romaneio_id}/comissao/${h.pagamento_index}`);
                                          }
                                          toast.success('Lançamento excluído');
                                          await fetchSellerRomaneios(selectedSeller);
                                          await refreshSelectedSeller();
                                        } catch { toast.error('Erro ao excluir'); }
                                      }}
                                      className="text-red-500 hover:text-red-700 p-1"
                                      title="Excluir"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </>
                );
              })()}

              <div className="px-6 py-3 border-t border-border bg-muted/50 flex justify-end">
                <button onClick={() => setShowHistoricoModal(false)} className="btn-secondary">
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Detalhes/Edição de Comissão por Item */}
        {showComissaoModal && selectedRomaneio && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div 
              className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-border bg-muted/50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">Detalhes da Comissão</h3>
                    <p className="text-sm text-muted-foreground">
                      Pedido #{selectedRomaneio.numero_pedido} | Cliente: {selectedRomaneio.cliente_nome}
                    </p>
                  </div>
                  <button onClick={() => setShowComissaoModal(false)} className="p-2 hover:bg-accent rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                {/* Resumo */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Valor Venda</p>
                    <p className="font-bold text-lg">{formatCurrency(selectedRomaneio.valor_total)}</p>
                  </div>
                  <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Comissão Total</p>
                    <p className="font-bold text-lg text-orange-600">{formatCurrency(selectedRomaneio.comissao_calculada)}</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Já Pago</p>
                    <p className="font-bold text-lg text-green-600">{formatCurrency(selectedRomaneio.comissao_paga)}</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Pendente</p>
                    <p className="font-bold text-lg text-red-600">{formatCurrency(selectedRomaneio.comissao_pendente)}</p>
                  </div>
                </div>

                {/* Tabela de Itens com Comissão Editável */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Percent className="w-4 h-4" />
                    Comissão por Item
                  </h4>
                  <div className="border border-border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-3 py-2 text-left">Produto</th>
                          <th className="px-3 py-2 text-center">Qtd</th>
                          <th className="px-3 py-2 text-right">Valor Unit.</th>
                          <th className="px-3 py-2 text-right">Valor Total</th>
                          <th className="px-3 py-2 text-center">% Comissão</th>
                          <th className="px-3 py-2 text-right">Comissão R$</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {comissaoEditando?.items?.map((item, idx) => {
                          const comissaoItem = (item.valor_total || 0) * (parseFloat(item.percentual_comissao_edit) || 0) / 100;
                          return (
                            <tr key={idx}>
                              <td className="px-3 py-2 font-medium">{item.produto_nome || '-'}</td>
                              <td className="px-3 py-2 text-center">{item.quantidade}</td>
                              <td className="px-3 py-2 text-right">{formatCurrency(item.valor_unitario)}</td>
                              <td className="px-3 py-2 text-right">{formatCurrency(item.valor_total)}</td>
                              <td className="px-3 py-2 text-center">
                                <input
                                  type="number"
                                  value={item.percentual_comissao_edit || 0}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    setComissaoEditando(prev => ({
                                      ...prev,
                                      items: prev.items.map((it, i) => 
                                        i === idx ? { ...it, percentual_comissao_edit: newValue } : it
                                      )
                                    }));
                                  }}
                                  className="w-16 px-2 py-1 text-center border border-border rounded bg-background"
                                  step="0.5"
                                  min="0"
                                  max="100"
                                />
                                <span className="text-muted-foreground ml-1">%</span>
                              </td>
                              <td className="px-3 py-2 text-right font-medium text-orange-600">
                                {formatCurrency(comissaoItem)}
                              </td>
                            </tr>
                          );
                        })}
                        {(!comissaoEditando?.items || comissaoEditando.items.length === 0) && (
                          <tr>
                            <td colSpan="6" className="px-3 py-4 text-center text-muted-foreground">
                              Nenhum item encontrado neste romaneio
                            </td>
                          </tr>
                        )}
                      </tbody>
                      {comissaoEditando?.items?.length > 0 && (
                        <tfoot className="bg-muted/50">
                          <tr>
                            <td colSpan="5" className="px-3 py-2 text-right font-bold">
                              Total Comissão:
                            </td>
                            <td className="px-3 py-2 text-right font-bold text-orange-600">
                              {formatCurrency(
                                comissaoEditando.items.reduce((acc, item) => 
                                  acc + ((item.valor_total || 0) * (parseFloat(item.percentual_comissao_edit) || 0) / 100), 0
                                )
                              )}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>

                {/* Histórico de Pagamentos */}
                {selectedRomaneio.historico_comissoes?.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Histórico de Pagamentos ({selectedRomaneio.historico_comissoes.length})
                    </h4>
                    <div className="border border-border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-green-100 dark:bg-green-900/30">
                          <tr>
                            <th className="px-3 py-2 text-left">Data</th>
                            <th className="px-3 py-2 text-right">Valor</th>
                            <th className="px-3 py-2 text-left">Forma Pgto</th>
                            <th className="px-3 py-2 text-left">Observação</th>
                            <th className="px-3 py-2 text-center">Comprovante</th>
                            <th className="px-3 py-2 text-center w-20">Ações</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {selectedRomaneio.historico_comissoes.map((pag, idx) => (
                            <tr key={idx} className="bg-green-50/50 dark:bg-green-900/10">
                              <td className="px-3 py-2">{new Date(pag.data).toLocaleDateString('pt-BR')}</td>
                              <td className="px-3 py-2 text-right font-semibold text-green-600">
                                {formatCurrency(pag.valor)}
                              </td>
                              <td className="px-3 py-2 capitalize">{pag.forma_pagamento || '-'}</td>
                              <td className="px-3 py-2 text-muted-foreground text-xs">{pag.observacao || '-'}</td>
                              <td className="px-3 py-2 text-center">
                                <ComprovanteCell
                                  romaneioId={selectedRomaneio.id}
                                  paymentType="comissao"
                                  paymentIndex={idx}
                                />
                              </td>
                              <td className="px-3 py-2 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <button
                                    data-testid={`edit-comissao-pagamento-${idx}`}
                                    onClick={async () => {
                                      const valorAtual = (pag.valor || 0).toFixed(2).replace('.', ',');
                                      const input = window.prompt(`Editar valor do pagamento de ${new Date(pag.data).toLocaleDateString('pt-BR')}:\n\n(Use vírgula ou ponto como separador decimal)`, valorAtual);
                                      if (input === null) return;
                                      const novoValor = parseFloat(input.replace(',', '.'));
                                      if (isNaN(novoValor) || novoValor < 0) {
                                        toast.error('Valor inválido');
                                        return;
                                      }
                                      try {
                                        await axios.put(`${API}/romaneios/${selectedRomaneio.id}/comissao/${idx}`, { valor: novoValor });
                                        toast.success('Pagamento atualizado');
                                        await fetchSellerRomaneios(selectedSeller);
                                        await refreshSelectedSeller();
                                        setShowComissaoModal(false);
                                      } catch (err) { toast.error('Erro ao atualizar'); }
                                    }}
                                    className="text-blue-500 hover:text-blue-700 p-1"
                                    title="Editar valor"
                                  >
                                    <Edit className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    data-testid={`delete-comissao-pagamento-${idx}`}
                                    onClick={async () => {
                                      if (!window.confirm(`Remover pagamento de ${formatCurrency(pag.valor)}?`)) return;
                                      try {
                                        await axios.delete(`${API}/romaneios/${selectedRomaneio.id}/comissao/${idx}`);
                                        toast.success('Pagamento removido');
                                        await fetchSellerRomaneios(selectedSeller);
                                        await refreshSelectedSeller();
                                        setShowComissaoModal(false);
                                      } catch (err) { toast.error('Erro ao remover'); }
                                    }}
                                    className="text-red-500 hover:text-red-700 p-1"
                                    title="Remover pagamento"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 border-t border-border bg-muted flex items-center justify-end gap-3">
                <button onClick={() => setShowComissaoModal(false)} className="btn-secondary">
                  Cancelar
                </button>
                <button onClick={handleSaveComissaoItems} className="btn-primary flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Salvar Alterações
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Registrar Pagamento de Comissão */}
        {showPagamentoModal && selectedRomaneio && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div 
              className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-lg"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-border">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Registrar Pagamento de Comissão
                </h3>
                <p className="text-sm text-muted-foreground">
                  Pedido #{selectedRomaneio.numero_pedido} | {selectedSeller.nome}
                </p>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Resumo */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Comissão Total</p>
                    <p className="font-bold text-orange-600">{formatCurrency(selectedRomaneio.comissao_calculada)}</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Já Pago</p>
                    <p className="font-bold text-green-600">{formatCurrency(selectedRomaneio.comissao_paga)}</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center">
                    <p className="text-xs text-muted-foreground">Pendente</p>
                    <p className="font-bold text-red-600">{formatCurrency(selectedRomaneio.comissao_pendente)}</p>
                  </div>
                </div>

                {/* Histórico anterior */}
                {selectedRomaneio.historico_comissoes?.length > 0 && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                    <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-2 flex items-center gap-1">
                      <History className="w-3 h-3" />
                      Pagamentos Anteriores
                    </p>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                      {selectedRomaneio.historico_comissoes.map((pag, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            {new Date(pag.data).toLocaleDateString('pt-BR')}
                          </span>
                          <span className="font-semibold text-green-600">
                            {formatCurrency(pag.valor)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Formulário */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Valor a Pagar</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={pagamentoData.valor}
                      onChange={e => {
                        // Permitir apenas números, vírgula e ponto
                        const value = e.target.value.replace(/[^0-9.,]/g, '');
                        setPagamentoData(prev => ({ ...prev, valor: value }));
                      }}
                      placeholder="0,00"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Data</label>
                    <input
                      type="date"
                      value={pagamentoData.data}
                      onChange={e => setPagamentoData(prev => ({ ...prev, data: e.target.value }))}
                      className="input-field"
                    />
                  </div>
                </div>

                {/* Botões rápidos */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const valorPendente = selectedRomaneio?.comissao_pendente || 0;
                      setPagamentoData(prev => ({ 
                        ...prev, 
                        valor: valorPendente.toFixed(2).replace('.', ',')
                      }));
                    }}
                    className="flex-1 text-xs py-2 bg-green-100 hover:bg-green-200 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg transition-colors"
                  >
                    Quitar Total
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const valorMetade = (selectedRomaneio?.comissao_pendente || 0) / 2;
                      setPagamentoData(prev => ({ 
                        ...prev, 
                        valor: valorMetade.toFixed(2).replace('.', ',')
                      }));
                    }}
                    className="flex-1 text-xs py-2 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg transition-colors"
                  >
                    Metade (50%)
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Forma de Pagamento</label>
                  <select
                    value={pagamentoData.forma_pagamento}
                    onChange={e => setPagamentoData(prev => ({ ...prev, forma_pagamento: e.target.value }))}
                    className="input-field"
                  >
                    <option value="pix">PIX</option>
                    <option value="dinheiro">Dinheiro</option>
                    <option value="transferencia">Transferência</option>
                    <option value="cheque">Cheque</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Observação (opcional)</label>
                  <textarea
                    value={pagamentoData.observacao}
                    onChange={e => setPagamentoData(prev => ({ ...prev, observacao: e.target.value }))}
                    placeholder="Ex: Pagamento via PIX"
                    className="input-field resize-none"
                    rows={2}
                  />
                </div>
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 border-t border-border bg-muted flex items-center justify-end gap-3">
                <button onClick={() => setShowPagamentoModal(false)} className="btn-secondary">
                  Cancelar
                </button>
                <button 
                  onClick={handleRegistrarPagamento} 
                  className="btn-primary flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Confirmar Pagamento
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===================== LISTA DE VENDEDORES =====================
  return (
    <div className="space-y-6" data-testid="sellers-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-heading font-bold text-foreground">Vendedores</h2>
          <p className="text-muted-foreground mt-1">{sellers.length} vendedores cadastrados</p>
        </div>
        <button onClick={openNewModal} data-testid="add-seller-btn" className="btn-primary flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Novo Vendedor
        </button>
      </div>

      {/* Barra de Ações em Massa */}
      {selectedIds.length > 0 && (
        <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-primary">{selectedIds.length} vendedor(es) selecionado(s)</span>
            <button onClick={clearSelection} className="text-sm text-muted-foreground hover:text-foreground">Limpar</button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleExportCSV} className="btn-secondary flex items-center gap-2 text-sm py-2">
              <Download className="w-4 h-4" />
              Exportar CSV
            </button>
            <button onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm">
              <Trash2 className="w-4 h-4" />
              Excluir
            </button>
          </div>
        </div>
      )}

      {/* Cards de Resumo Geral (topo) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sellers-overview-cards">
        <div className="bg-card border border-border rounded-lg p-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground leading-tight">Total Vendas</p>
              <p className="text-lg font-bold leading-tight truncate">{formatCurrency(sellers.reduce((a, s) => a + (s.total_vendas || 0), 0))}</p>
              <p className="text-[10px] text-muted-foreground">{sellers.reduce((a, s) => a + (s.qtd_vendas || 0), 0)} romaneios</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <Percent className="w-4 h-4 text-orange-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground leading-tight">Comissão Devida</p>
              <p className="text-lg font-bold text-orange-600 leading-tight truncate">{formatCurrency(sellers.reduce((a, s) => a + (s.comissao_total_calculada || 0), 0))}</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground leading-tight">Já Pago</p>
              <p className="text-lg font-bold text-green-600 leading-tight truncate">{formatCurrency(sellers.reduce((a, s) => a + (s.comissao_paga_calculada || 0), 0))}</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-4 h-4 text-red-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground leading-tight">Pendente</p>
              <p className="text-lg font-bold text-red-600 leading-tight truncate">{formatCurrency(sellers.reduce((a, s) => a + (s.comissao_pendente_calculada || 0), 0))}</p>
            </div>
          </div>
        </div>
        <div className="bg-card border-2 border-purple-300 dark:border-purple-800 rounded-lg p-3 bg-purple-50/30 dark:bg-purple-900/10" data-testid="card-total-compras-vendedores">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <ShoppingCart className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-purple-700 dark:text-purple-300 leading-tight font-semibold">Total Compras dos Vendedores</p>
              <p className="text-lg font-bold text-purple-700 dark:text-purple-400 leading-tight truncate">
                {formatCurrency(sellers.reduce((a, s) => a + (s.total_compras_em_aberto || 0), 0))}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {sellers.reduce((a, s) => a + (s.qtd_compras_em_aberto || 0), 0)} romaneios em aberto / {sellers.reduce((a, s) => a + (s.qtd_compras || 0), 0)} totais
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Barra de Busca/Filtros — Animated (vendedores) */}
      <AnimatedSearchBar
        value={searchTermSellers}
        onChange={setSearchTermSellers}
        placeholder="Buscar por nome, WhatsApp ou e-mail..."
        activeFiltersCount={[sellersFilters.pendenteMin, sellersFilters.comComissao, sellersFilters.comCompras].filter(Boolean).length}
        onClearAll={() => {
          setSearchTermSellers('');
          setSellersFilters({ pendenteMin: '', comComissao: '', comCompras: '' });
        }}
        testIdPrefix="sellers"
        filtersPanel={
          <>
            <div>
              <label className="block text-xs font-medium mb-1 text-muted-foreground">Comissão pendente (mín. R$)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={sellersFilters.pendenteMin}
                onChange={(e) => setSellersFilters(prev => ({ ...prev, pendenteMin: e.target.value }))}
                placeholder="ex: 100"
                className="input-field text-sm"
                data-testid="sellers-filter-pendente-min"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1 text-muted-foreground">Com comissão devida?</label>
              <select
                value={sellersFilters.comComissao}
                onChange={(e) => setSellersFilters(prev => ({ ...prev, comComissao: e.target.value }))}
                className="input-field text-sm"
                data-testid="sellers-filter-com-comissao"
              >
                <option value="">Todos</option>
                <option value="sim">Apenas com comissão pendente</option>
                <option value="nao">Apenas sem comissão pendente</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1 text-muted-foreground">Com compras em aberto?</label>
              <select
                value={sellersFilters.comCompras}
                onChange={(e) => setSellersFilters(prev => ({ ...prev, comCompras: e.target.value }))}
                className="input-field text-sm"
                data-testid="sellers-filter-com-compras"
              >
                <option value="">Todos</option>
                <option value="sim">Apenas com compras em aberto</option>
                <option value="nao">Apenas sem compras em aberto</option>
              </select>
            </div>
          </>
        }
      />

      {/* Tabela de Vendedores */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold w-12">
                  <button onClick={toggleSelectAll} className="p-1 hover:bg-accent rounded">
                    {selectAll ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-muted-foreground" />}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Nome</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">WhatsApp</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">% Comissão</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Total Vendas</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Comissão Devida</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Pago</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-red-500">Pendente</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-purple-600 dark:text-purple-400">Compras</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(() => {
                const term = (searchTermSellers || '').toLowerCase().trim();
                const pendMin = parseFloat(sellersFilters.pendenteMin) || 0;
                const filtered = sellers.filter(s => {
                  if (term) {
                    const haystack = `${s.nome || ''} ${s.whatsapp || ''} ${s.email || ''}`.toLowerCase();
                    if (!haystack.includes(term)) return false;
                  }
                  const pend = s.comissao_pendente_calculada || 0;
                  if (pendMin > 0 && pend < pendMin) return false;
                  if (sellersFilters.comComissao === 'sim' && pend <= 0) return false;
                  if (sellersFilters.comComissao === 'nao' && pend > 0) return false;
                  const compras = s.qtd_compras_em_aberto || 0;
                  if (sellersFilters.comCompras === 'sim' && compras <= 0) return false;
                  if (sellersFilters.comCompras === 'nao' && compras > 0) return false;
                  return true;
                });
                if (filtered.length === 0) {
                  return (
                    <tr><td colSpan={10} className="px-6 py-12 text-center text-sm text-muted-foreground" data-testid="sellers-empty">
                      Nenhum vendedor encontrado com esses filtros.
                    </td></tr>
                  );
                }
                return filtered.map((seller) => {
                const tooltipInfo = `${seller.nome}\n\n📊 Vendas: ${formatCurrency(seller.total_vendas || 0)} (${seller.qtd_vendas || 0} romaneios)\n💰 Comissão devida: ${formatCurrency(seller.comissao_total_calculada || 0)}\n✅ Comissão paga: ${formatCurrency(seller.comissao_paga_calculada || 0)}\n⏳ Comissão pendente: ${formatCurrency(seller.comissao_pendente_calculada || 0)}\n\n🛒 Total comprado: ${formatCurrency(seller.total_compras || 0)} (${seller.qtd_compras || 0} romaneios)\n🔴 Em aberto p/ pagar: ${formatCurrency(seller.total_compras_em_aberto || 0)} (${seller.qtd_compras_em_aberto || 0} pendentes)\n\n👉 Clique para ver detalhes`;
                return (
                <tr 
                  key={seller.id} 
                  className={`hover:bg-accent/50 transition-colors cursor-pointer ${selectedIds.includes(seller.id) ? 'bg-primary/5' : ''}`}
                  onClick={() => handleViewSeller(seller)}
                  title={tooltipInfo}
                  data-testid={`seller-row-${seller.id}`}
                >
                  <td className="px-4 py-4 text-sm" onClick={e => e.stopPropagation()}>
                    <button onClick={() => toggleSelectOne(seller.id)} className="p-1 hover:bg-accent rounded">
                      {selectedIds.includes(seller.id) ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-muted-foreground" />}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          {seller.nome}
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div className="text-xs text-muted-foreground">{seller.qtd_vendas || 0} vendas</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">{seller.whatsapp || '-'}</td>
                  <td className="px-6 py-4 text-sm text-center">
                    <span className="bg-primary/10 text-primary px-2 py-1 rounded font-medium">
                      {seller.percentual_comissao || 0}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-right font-medium">
                    {formatCurrency(seller.total_vendas)}
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-orange-600 font-medium">
                    {formatCurrency(seller.comissao_total_calculada)}
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-green-600">
                    {formatCurrency(seller.comissao_paga_calculada)}
                  </td>
                  <td className="px-6 py-4 text-sm text-right">
                    <span className={`font-bold ${(seller.comissao_pendente_calculada || 0) > 0 ? 'text-red-500' : 'text-green-500'}`}>
                      {formatCurrency(seller.comissao_pendente_calculada)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-right">
                    <div className="flex flex-col items-end">
                      <span className={`font-bold ${(seller.total_compras_em_aberto || 0) > 0 ? 'text-purple-600 dark:text-purple-400' : 'text-muted-foreground'}`}>
                        {formatCurrency(seller.total_compras_em_aberto || 0)}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {(seller.qtd_compras_em_aberto || 0) > 0
                          ? `${seller.qtd_compras_em_aberto} em aberto`
                          : `${seller.qtd_compras || 0} total`}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-center" onClick={e => e.stopPropagation()}>
                    <button onClick={() => handleConcederAcesso(seller)} className="text-blue-600 hover:text-blue-800 mr-3" title="Conceder acesso ao Painel do Vendedor" data-testid={`btn-conceder-acesso-${seller.id}`}>
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleGenerateLink(seller)} className="text-green-600 hover:text-green-800 mr-3" title="Gerar Link de Pedido">
                      <Link2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => openEditModal(seller)} className="text-primary hover:text-primary/80 mr-3" title="Editar">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(seller.id)} className="text-red-600 hover:text-red-800" title="Deletar">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
                );
              });
              })()}
              {sellers.length === 0 && (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-muted-foreground">
                    Nenhum vendedor cadastrado
                  </td>
                </tr>
              )}
            </tbody>
            {/* Totais */}
            {sellers.length > 0 && (
              <tfoot className="bg-muted/50 border-t border-border">
                <tr>
                  <td colSpan="4" className="px-6 py-3 text-sm font-bold text-right">TOTAIS:</td>
                  <td className="px-6 py-3 text-sm text-right font-bold">
                    {formatCurrency(sellers.reduce((acc, s) => acc + (s.total_vendas || 0), 0))}
                  </td>
                  <td className="px-6 py-3 text-sm text-right font-bold text-orange-600">
                    {formatCurrency(sellers.reduce((acc, s) => acc + (s.comissao_total_calculada || 0), 0))}
                  </td>
                  <td className="px-6 py-3 text-sm text-right font-bold text-green-600">
                    {formatCurrency(sellers.reduce((acc, s) => acc + (s.comissao_paga_calculada || 0), 0))}
                  </td>
                  <td className="px-6 py-3 text-sm text-right font-bold text-red-500">
                    {formatCurrency(sellers.reduce((acc, s) => acc + (s.comissao_pendente_calculada || 0), 0))}
                  </td>
                  <td className="px-6 py-3 text-sm text-right font-bold text-purple-600 dark:text-purple-400">
                    {formatCurrency(sellers.reduce((acc, s) => acc + (s.total_compras_em_aberto || 0), 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Modal de Edição/Criação de Vendedor */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-semibold">{editingSeller ? 'Editar Vendedor' : 'Novo Vendedor'}</h3>
              <button onClick={closeModal} className="p-2 hover:bg-accent rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Nome *</label>
                  <input
                    type="text"
                    required
                    value={formData.nome}
                    onChange={e => setFormData(prev => ({ ...prev, nome: e.target.value }))}
                    className="input-field"
                    placeholder="Nome do vendedor"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">WhatsApp</label>
                  <input
                    type="text"
                    value={formData.whatsapp}
                    onChange={e => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
                    className="input-field"
                    placeholder="(11) 99999-9999"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Telefone</label>
                  <input
                    type="text"
                    value={formData.telefone}
                    onChange={e => setFormData(prev => ({ ...prev, telefone: e.target.value }))}
                    className="input-field"
                    placeholder="(11) 3333-3333"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="input-field"
                    placeholder="email@exemplo.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">% Comissão Padrão</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={formData.percentual_comissao}
                    onChange={e => setFormData(prev => ({ ...prev, percentual_comissao: e.target.value }))}
                    className="input-field"
                    placeholder="Ex: 10"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Endereço</label>
                  <input
                    type="text"
                    value={formData.endereco}
                    onChange={e => setFormData(prev => ({ ...prev, endereco: e.target.value }))}
                    className="input-field"
                    placeholder="Endereço completo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Conta Bancária</label>
                  <input
                    type="text"
                    value={formData.conta_bancaria}
                    onChange={e => setFormData(prev => ({ ...prev, conta_bancaria: e.target.value }))}
                    className="input-field"
                    placeholder="Banco / Agência / Conta"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Chave PIX</label>
                  <input
                    type="text"
                    value={formData.chave_pix}
                    onChange={e => setFormData(prev => ({ ...prev, chave_pix: e.target.value }))}
                    className="input-field"
                    placeholder="CPF, Email ou Telefone"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <button type="button" onClick={closeModal} className="btn-secondary">Cancelar</button>
                <button type="submit" className="btn-primary">
                  {editingSeller ? 'Salvar Alterações' : 'Criar Vendedor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL EDIÇÃO RÁPIDA DE ROMANEIO DE COMPRA (inline, não sai da página) */}
      {editingRomaneioInline && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="edit-romaneio-inline-modal">
          <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-purple-50 dark:bg-purple-900/20">
              <div>
                <h3 className="text-base font-bold">Editar Romaneio #{editingRomaneioInline.numero_pedido}</h3>
                <p className="text-xs text-muted-foreground">Compra de {selectedSeller?.nome}</p>
              </div>
              <button onClick={() => setEditingRomaneioInline(null)} className="p-1.5 hover:bg-accent rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1">Cliente</label>
                <input
                  type="text"
                  value={editFormInline.cliente_nome}
                  onChange={(e) => setEditFormInline(prev => ({ ...prev, cliente_nome: e.target.value }))}
                  className="input-field text-sm"
                  data-testid="edit-inline-cliente"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Valor Total (R$)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editFormInline.valor_total}
                    onChange={(e) => setEditFormInline(prev => ({ ...prev, valor_total: e.target.value }))}
                    className="input-field text-sm"
                    data-testid="edit-inline-valor"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">NF</label>
                  <input
                    type="text"
                    value={editFormInline.numero_nota_fiscal}
                    onChange={(e) => setEditFormInline(prev => ({ ...prev, numero_nota_fiscal: e.target.value }))}
                    className="input-field text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Forma de Pagamento</label>
                <select
                  value={editFormInline.forma_pagamento}
                  onChange={(e) => setEditFormInline(prev => ({ ...prev, forma_pagamento: e.target.value }))}
                  className="input-field text-sm"
                  data-testid="edit-inline-forma"
                >
                  <option value="a_receber">A Receber</option>
                  <option value="A Prazo">A Prazo</option>
                  <option value="pago">Pago</option>
                  <option value="avista">À Vista</option>
                  <option value="dinheiro">Dinheiro</option>
                  <option value="pix">PIX</option>
                  <option value="boleto">Boleto</option>
                  <option value="cheque">Cheque</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Observações</label>
                <textarea
                  value={editFormInline.observacoes}
                  onChange={(e) => setEditFormInline(prev => ({ ...prev, observacoes: e.target.value }))}
                  className="input-field text-sm h-16 resize-none"
                />
              </div>
              <p className="text-[11px] text-muted-foreground bg-muted/50 p-2 rounded">
                ℹ️ Para editar itens/produtos do romaneio, abra na página de Romaneios.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-border bg-muted/30 flex justify-end gap-2">
              <button onClick={() => setEditingRomaneioInline(null)} className="px-4 py-2 text-sm rounded hover:bg-accent">Cancelar</button>
              <button onClick={submitEditRomaneioInline} className="btn-primary px-4 py-2 text-sm" data-testid="edit-inline-submit">
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Conceder Acesso ao Painel do Vendedor */}
      {showConcederModal && concederSeller && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="conceder-acesso-modal">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-gradient-to-r from-blue-600/10 to-indigo-600/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600 rounded-lg">
                  <KeyRound className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-foreground">Conceder Acesso</h2>
                  <p className="text-xs text-muted-foreground">Vendedor: <span className="font-semibold">{concederSeller.nome}</span></p>
                </div>
              </div>
              <button onClick={closeConcederModal} className="p-1 hover:bg-accent rounded" data-testid="conceder-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            {!concederResult ? (
              <div className="p-6 space-y-5">
                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold mb-2">E-mail do vendedor</label>
                  <input
                    type="email"
                    autoFocus
                    value={concederForm.email}
                    onChange={(e) => setConcederForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="exemplo@email.com"
                    className="input-field w-full"
                    data-testid="conceder-email-input"
                  />
                </div>

                {/* Método de senha */}
                <div>
                  <label className="block text-sm font-semibold mb-3">Como deseja definir a senha?</label>
                  <div className="space-y-2">
                    {/* Opção 1: Manual */}
                    <label
                      className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        concederForm.metodo === 'manual' ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'border-border hover:border-blue-300'
                      }`}
                      data-testid="conceder-metodo-manual"
                    >
                      <input
                        type="radio"
                        name="metodo"
                        value="manual"
                        checked={concederForm.metodo === 'manual'}
                        onChange={() => setConcederForm(prev => ({ ...prev, metodo: 'manual' }))}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 font-medium text-sm">
                          <UserPlus className="w-4 h-4 text-blue-600" />
                          Eu defino a senha
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">Você cria uma senha personalizada e a passa pessoalmente ao vendedor.</p>
                        {concederForm.metodo === 'manual' && (
                          <div className="mt-2 relative">
                            <input
                              type={concederForm.showPassword ? 'text' : 'password'}
                              value={concederForm.senha_personalizada}
                              onChange={(e) => setConcederForm(prev => ({ ...prev, senha_personalizada: e.target.value }))}
                              placeholder="Digite a senha (mín. 4 caracteres)"
                              className="input-field w-full pr-10 text-sm"
                              data-testid="conceder-senha-manual-input"
                            />
                            <button
                              type="button"
                              onClick={() => setConcederForm(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-accent rounded"
                            >
                              {concederForm.showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        )}
                      </div>
                    </label>

                    {/* Opção 2: Auto + copiar */}
                    <label
                      className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        concederForm.metodo === 'auto_copiar' ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'border-border hover:border-blue-300'
                      }`}
                      data-testid="conceder-metodo-auto-copiar"
                    >
                      <input
                        type="radio"
                        name="metodo"
                        value="auto_copiar"
                        checked={concederForm.metodo === 'auto_copiar'}
                        onChange={() => setConcederForm(prev => ({ ...prev, metodo: 'auto_copiar' }))}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 font-medium text-sm">
                          <Shuffle className="w-4 h-4 text-purple-600" />
                          Sistema gera senha aleatória — eu copio e envio
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">O sistema gera uma senha forte; você copia e envia pelo seu canal (WhatsApp, ligação, etc).</p>
                      </div>
                    </label>

                    {/* Opção 3: Auto + email */}
                    <label
                      className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        concederForm.metodo === 'auto_email' ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'border-border hover:border-blue-300'
                      }`}
                      data-testid="conceder-metodo-auto-email"
                    >
                      <input
                        type="radio"
                        name="metodo"
                        value="auto_email"
                        checked={concederForm.metodo === 'auto_email'}
                        onChange={() => setConcederForm(prev => ({ ...prev, metodo: 'auto_email' }))}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 font-medium text-sm">
                          <Mail className="w-4 h-4 text-green-600" />
                          Sistema gera e envia por e-mail
                          <span className="text-[10px] bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full">Recomendado</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">O sistema gera uma senha forte e envia automaticamente para o e-mail do vendedor.</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Info no 1º login */}
                {concederForm.metodo !== 'manual' && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-800 dark:text-amber-300">
                    <strong>ℹ️ No primeiro login</strong>, o vendedor será obrigado a trocar essa senha por uma pessoal.
                  </div>
                )}
                {concederForm.metodo === 'manual' && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-xs text-blue-800 dark:text-blue-300">
                    <strong>ℹ️ Senha manual:</strong> o vendedor poderá usar essa senha diretamente, sem forçar troca no 1º login.
                  </div>
                )}
              </div>
            ) : (
              /* RESULTADO */
              <div className="p-6 space-y-4">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 font-bold text-green-700 dark:text-green-400 mb-1">
                    <Check className="w-5 h-5" />
                    Acesso {concederResult.action} com sucesso!
                  </div>
                  {concederResult.metodo === 'auto_email' && (
                    concederResult.email_enviado ? (
                      <p className="text-sm text-green-700 dark:text-green-400">
                        ✉️ E-mail enviado para <b>{concederResult.email}</b> com as credenciais.
                      </p>
                    ) : (
                      <p className="text-sm text-amber-700 dark:text-amber-400">
                        ⚠️ E-mail não foi enviado ({concederResult.erro_email || 'SMTP não configurado'}). Copie e repasse manualmente:
                      </p>
                    )
                  )}
                </div>

                {/* Credenciais */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1">E-MAIL</label>
                    <div className="flex items-center gap-2 p-2 bg-muted rounded border border-border">
                      <code className="flex-1 text-sm">{concederResult.email}</code>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1">SENHA</label>
                    <div className="flex items-center gap-2 p-2 bg-muted rounded border border-border">
                      <code className="flex-1 text-sm font-bold tracking-wider">{concederResult.senha}</code>
                      <button
                        onClick={() => copiarCredenciais('senha')}
                        className="p-1.5 hover:bg-accent rounded text-blue-600"
                        title="Copiar apenas a senha"
                        data-testid="conceder-copiar-senha-btn"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1">LINK DE ACESSO</label>
                    <div className="flex items-center gap-2 p-2 bg-muted rounded border border-border">
                      <code className="flex-1 text-xs truncate">{window.location.origin}/vendedor/login</code>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => copiarCredenciais('completo')}
                  className="w-full btn-primary py-2.5 flex items-center justify-center gap-2 text-sm font-semibold"
                  data-testid="conceder-copiar-tudo-btn"
                >
                  <Copy className="w-4 h-4" />
                  Copiar credenciais completas (e-mail + senha + link)
                </button>

                {concederResult.must_change_password && (
                  <p className="text-xs text-muted-foreground text-center">
                    ℹ️ No primeiro login, o vendedor será obrigado a trocar essa senha.
                  </p>
                )}
              </div>
            )}

            {/* Footer */}
            <div className="px-6 py-3 border-t border-border bg-muted/30 flex justify-end gap-2">
              {!concederResult ? (
                <>
                  <button onClick={closeConcederModal} className="px-4 py-2 text-sm rounded hover:bg-accent" data-testid="conceder-cancelar-btn">
                    Cancelar
                  </button>
                  <button
                    onClick={submitConcederAcesso}
                    disabled={concederLoading}
                    className="btn-primary px-5 py-2 text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
                    data-testid="conceder-confirmar-btn"
                  >
                    {concederLoading ? 'Processando...' : (
                      <>
                        <KeyRound className="w-4 h-4" />
                        Conceder Acesso
                      </>
                    )}
                  </button>
                </>
              ) : (
                <button
                  onClick={closeConcederModal}
                  className="btn-primary px-5 py-2 text-sm font-semibold"
                  data-testid="conceder-fechar-btn"
                >
                  Concluir
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sellers;
